import React, { useEffect, useMemo, useState } from 'react';
import { useUserPrefs } from '../../Components/context/UserPrefs';
import { Modal } from '../../Components/Modal/Modal';
import { Save, Search, ChevronLeft, ChevronRight, Calendar, Plus, PlusCircle, Trash, Home, ClipboardList, MapPin, RefreshCcw, Clock, User, Camera, Key, Mail, CheckCircle, AlertCircle, Trash2, Pencil, Download, Lightbulb } from 'lucide-react';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import * as XLSX from 'xlsx';
import { api, setCurrentPageContext } from '../../services/api';
import ControlOperativoDashboard from './ControlOperativoDashboard';
import SectionIndexCards from '../../Components/Common/SectionIndexCards';
import { isNativeRuntime } from '../../utils/runtimePlatform';
import { roleDocuments, personOperationalRole } from '../../utils/operationalRole';
import senerfiBlanco from '../../assets/images/senerfiblanco.png';
import sincronizarIcon from '../../assets/images/sincronizar.svg';
import pendienteIcon from '../../assets/images/pendiente.svg';
import enProcesoIcon from '../../assets/images/en proceso.svg';
import completadaIcon from '../../assets/images/completada.svg';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

export default function ControlOperativoPanel({ location, showLocation = true, mobileMode = false }) {
  const { users = [], currentUser } = useUserPrefs();
  const displayNames = {
    'popayan': 'Popayán',
    'santander-de-quilichao': 'Santander de Quilichao',
  };
  const title = displayNames[location] || location;
  const [section, setSection] = useState('planificacion');
  // Reporta a Administración TI > Auditoría qué submódulo de Control
  // Operativo está viendo el usuario (ver X-Senerfi-Page en src/services/api.js).
  useEffect(() => {
    setCurrentPageContext(`/sede/${location || ''}/control-operativo/${section || ''}`);
  }, [location, section]);
  const [coGroup, setCoGroup] = useState('linea_viva');
  const [mobileTab, setMobileTab] = useState('inicio');
  const [mobileBrigId, setMobileBrigId] = useState('');
  const [mapLoc, setMapLoc] = useState(null);
  useEffect(() => {
    if (mobileTab !== 'mapa') return;
    let id = null;
    try {
      id = navigator.geolocation && navigator.geolocation.watchPosition ? navigator.geolocation.watchPosition((p) => setMapLoc({ lat: p.coords.latitude, lng: p.coords.longitude }), () => {}, { enableHighAccuracy: true }) : null;
    } catch {}
    return () => { try { if (id && navigator.geolocation && navigator.geolocation.clearWatch) navigator.geolocation.clearWatch(id); } catch {} };
  }, [mobileTab]);
  const [isEditBrigOpen, setIsEditBrigOpen] = useState(false);
  const [editBrigId, setEditBrigId] = useState('');
  const [editBrigForm, setEditBrigForm] = useState({ name: '', description: '', responsibleId: '', members: [] });
  const [configActs, setConfigActs] = useState([]);
  const [actForm, setActForm] = useState({ name: '', description: '', process_type: 'linea_viva', valor_unitario: '' });
  const [actEditId, setActEditId] = useState('');
  const [brigades, setBrigades] = useState([]);
  const [brigUsers, setBrigUsers] = useState([]);
  const [brigUsersLoading, setBrigUsersLoading] = useState(false);
  const [brigUsersMessage, setBrigUsersMessage] = useState(null);
  const [brigUserCedulaSearch, setBrigUserCedulaSearch] = useState('');
  const [brigUserFoundPerson, setBrigUserFoundPerson] = useState(null);
  const [brigUserForm, setBrigUserForm] = useState({ nombre: '', usuario: '', password: '', cedula: '', brigada: '', tipo_proceso: '' });
  const [brigUserEditId, setBrigUserEditId] = useState('');
  const [segOrders, setSegOrders] = useState([]);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pendingSync, setPendingSync] = useState(() => {
    try { const v = localStorage.getItem('co_pending_sync'); return v ? JSON.parse(v) : []; } catch { return []; }
  });
  const [lastSyncAt, setLastSyncAt] = useState(() => {
    try { return localStorage.getItem('co_last_sync_at') || ''; } catch { return ''; }
  });
  const [syncing, setSyncing] = useState(false);
  const [groupRecords, setGroupRecords] = useState([]);
  const [, setGroupLoading] = useState(false);
  const [groupForm, setGroupForm] = useState({ title: '', description: '', status: 'Pendiente', date: '', documentedPersonId: '' });
  const [docPeople, setDocPeople] = useState([]);
  const [docPeopleLoading, setDocPeopleLoading] = useState(false);
  const [sessionDocumentedPerson, setSessionDocumentedPerson] = useState(null);
  const [actionPerms, setActionPerms] = useState({
    'action.control_operativo.documentar_proyecto': false,
    'action.almacen.ejecutar_asignacion': false,
  });
  const canDocumentarProyecto = Boolean(actionPerms['action.control_operativo.documentar_proyecto']);
  const canEjecutarAsignacionAlmacen = Boolean(actionPerms['action.almacen.ejecutar_asignacion']);
  const formatCOP = (value) => {
    if (value === '' || value === null || value === undefined) {
      try {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(0);
      } catch {
        return '$ 0';
      }
    }
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    try {
      return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(num);
    } catch {
      return `$ ${num}`;
    }
  };
  const normalizeConfigActs = (list) => {
    if (!Array.isArray(list)) return [];
    const out = [];
    for (const raw of list) {
      let item = raw;
      if (typeof item === 'string') {
        try { item = JSON.parse(item); } catch { continue; }
      }
      if (!item || typeof item !== 'object') continue;
      const id = item.id ?? item.ID ?? item.Id ?? null;
      const name = String(item.name ?? item.codigo ?? item.code ?? '').trim();
      if (!id || !name) continue;
      out.push({
        id,
        name,
        description: String(item.description ?? '').trim(),
        process_type: String(item.process_type ?? item.tipo_proceso ?? 'linea_viva').trim() || 'linea_viva',
        valor_unitario: item.valor_unitario ?? '',
      });
    }
    return out;
  };
  const processLabel = (p) => {
    const v = String(p || '').trim();
    if (v === 'linea_viva') return 'Línea viva';
    if (v === 'proyectos') return 'Proyectos';
    if (v === 'gruas') return 'Gruas';
    if (v === 'alumbrado_publico') return 'Alumbrado público';
    return v || '-';
  };
  const [apTab, setApTab] = useState('reportes');
  const [apReportes, setApReportes] = useState([]);
  const [apReportesLoading, setApReportesLoading] = useState(false);
  const [, setApMat] = useState([]);
  const [, setApMatLoading] = useState(false);
  const [apValidCodigo, setApValidCodigo] = useState('');
  const [apValidLoading, setApValidLoading] = useState(false);
  const [apValidReporte, setApValidReporte] = useState(null);
  const [apValidMateriales, setApValidMateriales] = useState([]);
  const [apValidReporteEditing, setApValidReporteEditing] = useState(false);
  const [apValidReporteDraft, setApValidReporteDraft] = useState(null);
  const [apMatEditId, setApMatEditId] = useState('');
  const [apExportFrom, setApExportFrom] = useState('');
  const [apExportTo, setApExportTo] = useState('');
  const [apExportLoading, setApExportLoading] = useState(false);
  const [isApReporteOpen, setIsApReporteOpen] = useState(false);
  const [apReportesHistory, setApReportesHistory] = useState(() => {
    try {
      const raw = localStorage.getItem('ap_reportes_history');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
  });
  const [apReporteSaveError, setApReporteSaveError] = useState('');
  const [apReporteForm, setApReporteForm] = useState({
    codigo: '',
    estado_reporte: 'Pendiente',
    comuna: '',
    barrio: '',
    tipo_dano: '',
    nodo_reportado: '',
    latitud: '',
    longitud: '',
    tipo_replanteo: '',
    fecha_limite: '',
    grupo_ejecutor: ''
  });
  const [apMatForm, setApMatForm] = useState({
    codigo: '',
    instala_material: 'Si',
    material_item: '',
    cantidad: '',
    unidad: '',
    aplica_serial: 'No',
    numero_serial: '',
    observaciones: '',
    nodo_instalacion: '',
    hora_inicio: '',
    hora_fin: '',
    foto_instalado: '',
    coordenada: ''
  });

  const apReportesTableRows = useMemo(() => {
    const byId = new Map((apReportesHistory || []).map((h) => [String(h?.id), h]));
    return (apReportes || []).map((r) => {
      const hx = byId.get(String(r?.id));
      const createdRaw = r?.created_at || r?.createdAt || hx?.savedAt || '';
      const creado = createdRaw ? String(createdRaw).slice(0, 19).replace('T', ' ') : '-';
      return {
        id: r?.id,
        creado,
        brigada: String(r?.grupo_ejecutor || hx?.grupo_ejecutor || '').trim() || '-',
        codigo: r?.codigo || '-',
        estadoReporte: r?.estado_reporte || '-',
        observaciones: r?.observaciones || hx?.observaciones || '-',
        comuna: r?.comuna || '-',
        barrio: r?.barrio || '-',
        tipoDano: r?.tipo_dano || '-',
        nodoReportado: r?.nodo_reportado || '-',
        latitud: r?.latitud || '-',
        longitud: r?.longitud || '-',
        tipoReplanteo: r?.tipo_replanteo || '-',
        fechaLimite: r?.fecha_limite || '-',
      };
    });
  }, [apReportes, apReportesHistory]);

  const exportApReportesTable = () => {
    const rows = apReportesTableRows.map((row) => ({
      CREADO: row.creado,
      BRIGADA: row.brigada,
      CODIGO: row.codigo,
      ESTADO_REPORTE: row.estadoReporte,
      OBSERVACIONES: row.observaciones,
      COMUNA: row.comuna,
      BARRIO: row.barrio,
      TIPO_DANO: row.tipoDano,
      NODO_REPORTADO: row.nodoReportado,
      LATITUD: row.latitud,
      LONGITUD: row.longitud,
      TIPO_REPLANTEO: row.tipoReplanteo,
      FECHA_LIMITE: row.fechaLimite,
    }));
    if (!rows.length) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Reportes');
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'control-operativo', reportName: 'Alumbrado público reportes' });
    XLSX.writeFile(wb, `alumbrado_publico_reportes_${location || 'sede'}.xlsx`);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.coProyectos.capabilities();
        if (res?.ok && res.permissions && typeof res.permissions === 'object') {
          setActionPerms((prev) => ({ ...prev, ...res.permissions }));
        }
        if (res?.ok && res.session_documented_person?.person_id) {
          setSessionDocumentedPerson(res.session_documented_person);
          setGroupForm((f) => (
            f.documentedPersonId
              ? f
              : { ...f, documentedPersonId: String(res.session_documented_person.person_id) }
          ));
        } else {
          setSessionDocumentedPerson(null);
        }
      } catch (_) {}
    })();
  }, []);

  const resolveGroupApi = (group) => {
    if (group === 'proyectos') return api.coProyectos;
    if (group === 'gruas') return api.coGruas;
    if (group === 'alumbrado_publico') return api.coAlumbrado;
    if (group === 'linea_viva') return api.coLineaViva;
    return api.coRecords;
  };

  useEffect(() => {
    if (mobileMode) return;
    if (!['proyectos', 'gruas'].includes(coGroup)) return;
    (async () => {
      setGroupLoading(true);
      try {
        const groupApi = resolveGroupApi(coGroup);
        const res = groupApi?.list ? await groupApi.list() : await api.coRecords.list(coGroup);
        if (res?.ok) setGroupRecords(res.data || []);
      } catch (_) {}
      setGroupLoading(false);
    })();
  }, [coGroup, mobileMode]);

  useEffect(() => {
    if (mobileMode || coGroup !== 'proyectos' || !canDocumentarProyecto) {
      setDocPeople([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setDocPeopleLoading(true);
      try {
        const res = await api.people.get({ limit: 1000, clean: 1, exclude_vehicle_like: 1 });
        const rows = Array.isArray(res?.data)
          ? res.data
          : (Array.isArray(res?.data?.data) ? res.data.data : []);
        const brigadistas = rows.filter((p) => {
          if (String(p?.status || '').trim().toLowerCase() !== 'activo') return false;
          return roleDocuments(personOperationalRole(p));
        });
        if (!cancelled) setDocPeople(brigadistas);
      } catch (_) {
        if (!cancelled) setDocPeople([]);
      } finally {
        if (!cancelled) setDocPeopleLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [coGroup, canDocumentarProyecto, mobileMode]);

  useEffect(() => {
    if (mobileMode) return;
    if (coGroup !== 'alumbrado_publico') return;
    (async () => {
      setApReportesLoading(true);
      try {
        const res = await api.alumbradoReportes.list();
        if (res?.ok) setApReportes(res.data || []);
      } catch (_) {
        try {
          const raw = localStorage.getItem('ap_reportes_history');
          const parsed = raw ? JSON.parse(raw) : [];
          if (Array.isArray(parsed) && parsed.length) {
            const fallback = parsed.map((x, idx) => ({ id: x?.id || `local-${idx}`, ...x }));
            setApReportes(fallback);
          }
        } catch (_) {}
      }
      setApReportesLoading(false);
    })();
    (async () => {
      setApMatLoading(true);
      try {
        const res = await api.alumbradoMatInstalado.list();
        if (res?.ok) setApMat(res.data || []);
      } catch (_) {}
      setApMatLoading(false);
    })();
  }, [coGroup, mobileMode]);

  const saveGroupRecord = async (e) => {
    e?.preventDefault?.();
    if (!groupForm.title.trim()) return;
    const docPid = Number(groupForm.documentedPersonId || sessionDocumentedPerson?.person_id || 0);
    if (coGroup === 'proyectos' && canDocumentarProyecto && (!docPid || docPid <= 0)) {
      setStatusMsg('No hay brigadista en sesión ni seleccionado. Configure responsible_cedula en su usuario o elija en el listado.');
      return;
    }
    try {
      const groupApi = resolveGroupApi(coGroup);
      if (groupApi?.upsert) {
        await groupApi.upsert({
          title: groupForm.title,
          description: groupForm.description,
          status: groupForm.status,
          date: groupForm.date || null,
          sede: location || '',
          co_group: coGroup === 'proyectos' ? 'proyectos' : coGroup,
          ...(coGroup === 'proyectos' && docPid > 0 ? { documented_person_id: docPid } : {}),
        });
      } else {
        await api.coRecords.upsert({ group: coGroup, title: groupForm.title, description: groupForm.description, status: groupForm.status });
      }
      const res = groupApi?.list ? await groupApi.list() : await api.coRecords.list(coGroup);
      if (res?.ok) setGroupRecords(res.data || []);
      setGroupForm({ title: '', description: '', status: 'Pendiente', date: '', documentedPersonId: '' });
    } catch (_) {}
  };

  const deleteGroupRecord = async (id) => {
    try {
      const groupApi = resolveGroupApi(coGroup);
      if (groupApi?.delete) {
        await groupApi.delete(id);
      } else {
        await api.coRecords.delete(coGroup, id);
      }
      setGroupRecords((l) => l.filter((r) => r.id !== id));
    } catch (_) {}
  };

  const saveApReporte = async (e) => {
    e?.preventDefault?.();
    if (!String(apReporteForm.codigo || '').trim() || !String(apReporteForm.fecha_limite || '').trim()) {
      setApReporteSaveError('Completa los campos obligatorios: Código y Fecha del reporte.');
      return;
    }
    setApReporteSaveError('');
    try {
      const payload = {
        ...apReporteForm,
        estado_reporte: String(apReporteForm.estado_reporte || 'Pendiente').trim() || 'Pendiente',
      };
      const up = await api.alumbradoReportes.upsert(payload);
      const res = await api.alumbradoReportes.list();
      if (res?.ok) setApReportes(res.data || []);
      const savedAt = new Date().toISOString();
      const entry = { ...payload, id: up?.id || payload.codigo, savedAt };
      const nextHistory = [entry, ...(apReportesHistory || [])].slice(0, 500);
      setApReportesHistory(nextHistory);
      try { localStorage.setItem('ap_reportes_history', JSON.stringify(nextHistory)); } catch (_) {}
      setApReporteForm({
        codigo: '',
        estado_reporte: 'Pendiente',
        comuna: '',
        barrio: '',
        tipo_dano: '',
        nodo_reportado: '',
        latitud: '',
        longitud: '',
        tipo_replanteo: '',
        fecha_limite: '',
        grupo_ejecutor: ''
      });
      setIsApReporteOpen(false);
    } catch (_) {
      setApReporteSaveError('No se pudo guardar el reporte. Verifica la conexión e intenta de nuevo.');
    }
  };

  const deleteApReporte = async (id) => {
    try {
      if (String(id).startsWith('local-')) {
        setApReportes((l) => l.filter((r) => r.id !== id));
        const nextHistory = (apReportesHistory || []).filter((r) => String(r?.id) !== String(id));
        setApReportesHistory(nextHistory);
        try { localStorage.setItem('ap_reportes_history', JSON.stringify(nextHistory)); } catch (_) {}
        return;
      }
      await api.alumbradoReportes.delete(id);
      setApReportes((l) => l.filter((r) => r.id !== id));
      const nextHistory = (apReportesHistory || []).filter((r) => String(r?.id) !== String(id));
      setApReportesHistory(nextHistory);
      try { localStorage.setItem('ap_reportes_history', JSON.stringify(nextHistory)); } catch (_) {}
    } catch (_) {}
  };

  const saveApMat = async (e) => {
    e?.preventDefault?.();
    if (!String(apMatForm.codigo || '').trim()) return;
    try {
      const codigo = String(apMatForm.codigo || '').trim();
      await api.alumbradoMatInstalado.upsert(apMatForm);
      try { await fetchApValidacion(codigo); } catch (_) {}
      setApMatForm({
        codigo,
        instala_material: 'Si',
        material_item: '',
        cantidad: '',
        unidad: '',
        aplica_serial: 'No',
        numero_serial: '',
        observaciones: '',
        nodo_instalacion: '',
        hora_inicio: '',
        hora_fin: '',
        foto_instalado: '',
        coordenada: ''
      });
      setApMatEditId('');
    } catch (_) {}
  };

  const deleteApMat = async (id) => {
    try {
      if (String(apMatEditId) === String(id)) {
        setApMatEditId('');
        setApMatForm((f) => ({ ...f, id: undefined }));
      }
      await api.alumbradoMatInstalado.delete(id);
      try { await fetchApValidacion(apValidCodigo); } catch (_) {}
    } catch (_) {}
  };

  const fetchApValidacion = async (codigo) => {
    const c = String(codigo || '').trim();
    if (!c) return;
    setApValidLoading(true);
    try {
      const res = await api.alumbradoValidacion.get(c);
      if (res?.ok) {
        const rep = res.reporte || null;
        setApValidReporte(rep);
        setApValidMateriales(res.materiales || []);
        if (!rep) {
          setApValidReporteDraft({
            codigo: c,
            estado_reporte: 'Pendiente',
            comuna: '',
            barrio: '',
            tipo_dano: '',
            nodo_reportado: '',
            latitud: '',
            longitud: '',
            fecha_limite: '',
            tipo_replanteo: '',
            nodo_real: '',
            coordenada_real: '',
            foto_referencia: '',
            observaciones: '',
            estado: '',
            grupo_ejecutor: ''
          });
          setApValidReporteEditing(true);
        } else if (!apValidReporteEditing) {
          setApValidReporteDraft(rep);
          setApValidReporteEditing(false);
        }
      }
    } catch (_) {}
    setApValidLoading(false);
  };

  const saveApValidReporte = async () => {
    const draft = apValidReporteDraft || null;
    if (!draft) return;
    const codigo = String(draft.codigo || apValidCodigo || '').trim();
    if (!codigo) return;
    try {
      const payload = {
        ...draft,
        codigo,
        estado_reporte: String(draft.estado_reporte || 'Pendiente').trim() || 'Pendiente',
      };
      await api.alumbradoReportes.upsert(payload);
      await fetchApValidacion(codigo);
      setApValidReporteEditing(false);
    } catch (_) {}
  };

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    try { window.addEventListener('online', on); } catch {}
    try { window.addEventListener('offline', off); } catch {}
    return () => {
      try { window.removeEventListener('online', on); } catch {}
      try { window.removeEventListener('offline', off); } catch {}
    };
  }, []);

  useEffect(() => {
    try { localStorage.setItem('co_pending_sync', JSON.stringify(pendingSync)); } catch {}
  }, [pendingSync]);

  const flagLocalChanges = () => {
    setPendingSync((prev) => {
      const has = prev.some((i) => i && i.type === 'local-changes');
      if (has) return prev;
      return [...prev, { type: 'local-changes', createdAt: Date.now() }];
    });
  };

  useEffect(() => {
    flagLocalChanges();
  }, [segOrders, brigades]);

  useEffect(() => {
    fetchBrigadeUsers();
  }, []);

  useEffect(() => {
    if (section === 'brigada') {
      fetchBrigadeUsers();
    }
  }, [section]);

  const processSyncQueue = React.useCallback(async () => {
    if (syncing) return;
    if (!isOnline) return;
    if (!pendingSync.length) return;
    setSyncing(true);
    try {
      await new Promise((r) => setTimeout(r, 600));
      setPendingSync([]);
      const ts = new Date().toISOString();
      setLastSyncAt(ts);
      try { localStorage.setItem('co_last_sync_at', ts); } catch {}
    } finally {
      setSyncing(false);
    }
  }, [isOnline, pendingSync, syncing]);

  useEffect(() => {
    if (isOnline) { processSyncQueue(); }
  }, [isOnline, processSyncQueue]);
  const [selectedCameraOrderId, setSelectedCameraOrderId] = useState('');
  const [cameraNodes, setCameraNodes] = useState([]);
  const [activeNodeIndex, setActiveNodeIndex] = useState(0);
  const [cameraStage, setCameraStage] = useState('antes');
  const cameraFileRef = React.useRef(null);
  const cameraCanvasRef = React.useRef(null);
  const [cameraLoc, setCameraLoc] = useState(null);
  const resolveDefaultBrigadeColor = (name) => {
    const key = String(name || '').trim().toUpperCase();
    if (key === 'BR-1') return '#FDE68A';
    if (key === 'BR-2') return '#A7F3D0';
    if (key === 'BR-3') return '#C4B5FD';
    if (key === 'BR-4') return '#FDBA74';
    if (key === 'BR-5') return '#93C5FD';
    if (key === 'BRIGADA') return '#FBCFE8';
    const palette = ['#FDE68A','#A7F3D0','#C4B5FD','#FDBA74','#93C5FD','#FBCFE8','#FFE4E6','#D1FAE5','#DDD6FE'];
    const idx = Math.abs(key.split('').reduce((a,c)=>a+c.charCodeAt(0),0)) % palette.length;
    return palette[idx];
  };

  const brigadeColor = (b) => {
    if (!b) return resolveDefaultBrigadeColor('');
    return b.color || resolveDefaultBrigadeColor(b.name || '');
  };

  const processTypeLabel = (v) => {
    const key = String(v || '').trim();
    if (key === 'linea_viva') return 'Línea viva';
    if (key === 'proyectos') return 'Proyectos';
    if (key === 'gruas') return 'Gruas';
    if (key === 'alumbrado_publico') return 'Alumbrado público';
    return key || '-';
  };

  const fetchBrigadeUsers = async () => {
    setBrigUsersLoading(true);
    try {
      const res = await api.brigadas.get();
      if (res && res.ok) {
        setBrigUsers(Array.isArray(res.data) ? res.data : []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBrigUsersLoading(false);
    }
  };

  const searchBrigUserPerson = async () => {
    if (!brigUserCedulaSearch) return;
    setBrigUsersLoading(true);
    setBrigUserFoundPerson(null);
    setBrigUsersMessage(null);
    try {
      const res = await api.people.get({ q: brigUserCedulaSearch });
      if (res && res.ok && res.data && res.data.length > 0) {
        const person = res.data.find((p) => String(p.id || p.dni) === String(brigUserCedulaSearch)) || res.data[0];
        const docId = person.id || person.dni;
        setBrigUserFoundPerson(person);
        setBrigUserForm({
          nombre: person.name,
          usuario: docId || '',
          password: '',
          cedula: docId,
          brigada: brigUserForm.brigada || '',
          tipo_proceso: brigUserForm.tipo_proceso || '',
        });
        setBrigUsersMessage({ type: 'success', text: `Persona encontrada: ${person.name}` });
      } else {
        setBrigUsersMessage({ type: 'error', text: 'No se encontró ninguna persona con esa cédula en People.' });
        setBrigUserFoundPerson(null);
        setBrigUserForm({
          nombre: '',
          usuario: brigUserCedulaSearch,
          password: '',
          cedula: brigUserCedulaSearch,
          brigada: brigUserForm.brigada || '',
          tipo_proceso: brigUserForm.tipo_proceso || '',
        });
      }
    } catch (error) {
      console.error(error);
      setBrigUsersMessage({ type: 'error', text: 'Error al buscar persona.' });
    } finally {
      setBrigUsersLoading(false);
    }
  };

  const handleCreateBrigUser = async (e) => {
    e.preventDefault();
    const isEdit = !!brigUserEditId;
    if (!brigUserForm.nombre || !brigUserForm.usuario || !brigUserForm.cedula || !brigUserForm.tipo_proceso || (!isEdit && !brigUserForm.password)) {
      setBrigUsersMessage({ type: 'error', text: 'Complete Nombre, Usuario, Cédula, Tipo de proceso y Contraseña (solo si es nuevo)' });
      return;
    }

    const existsCedula = brigUsers.some(u => String(u.cedula) === String(brigUserForm.cedula) && String(u.id) !== String(brigUserEditId));
    if (existsCedula) {
      setBrigUsersMessage({ type: 'error', text: 'Ya existe un usuario con esta Cédula' });
      return;
    }
    const existsUsuario = brigUsers.some(u => String(u.usuario).toLowerCase() === String(brigUserForm.usuario).toLowerCase() && String(u.id) !== String(brigUserEditId));
    if (existsUsuario) {
      setBrigUsersMessage({ type: 'error', text: 'Ya existe un usuario con este nombre de Usuario' });
      return;
    }

    setBrigUsersLoading(true);
    try {
      const payload = { ...brigUserForm, ...(brigUserEditId ? { id: brigUserEditId } : {}) };
      const res = await api.brigadas.upsert(payload);
      if (res && res.ok) {
        setBrigUsersMessage({ type: 'success', text: 'Usuario de brigada creado/actualizado' });
        setBrigUserForm({ nombre: '', usuario: '', password: '', cedula: '', brigada: '', tipo_proceso: '' });
        setBrigUserCedulaSearch('');
        setBrigUserFoundPerson(null);
        setBrigUserEditId('');
        fetchBrigadeUsers();
      } else {
        setBrigUsersMessage({ type: 'error', text: res.error || 'Error al guardar usuario de brigada' });
      }
    } catch (error) {
      console.error(error);
      const msg = error.message || 'Error de conexión';
      const cleanMsg = msg.replace(/^HTTP \d+: /, '');
      setBrigUsersMessage({ type: 'error', text: cleanMsg });
    } finally {
      setBrigUsersLoading(false);
    }
  };

  const startEditBrigUser = (u) => {
    setBrigUsersMessage(null);
    setBrigUserFoundPerson(null);
    setBrigUserCedulaSearch(String(u?.cedula || ''));
    setBrigUserEditId(String(u?.id || ''));
    setBrigUserForm({
      nombre: u?.nombre || '',
      usuario: u?.usuario || '',
      password: '',
      cedula: u?.cedula || '',
      brigada: u?.brigada || '',
      tipo_proceso: u?.tipo_proceso || '',
    });
  };

  const cancelEditBrigUser = () => {
    setBrigUsersMessage(null);
    setBrigUserFoundPerson(null);
    setBrigUserEditId('');
    setBrigUserCedulaSearch('');
    setBrigUserForm({ nombre: '', usuario: '', password: '', cedula: '', brigada: '', tipo_proceso: '' });
  };

  const handleDeleteBrigUser = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario de brigada?')) return;
    setBrigUsersLoading(true);
    try {
      const res = await api.brigadas.delete({ id });
      if (res && res.ok) {
        fetchBrigadeUsers();
      } else {
        alert('Error al eliminar usuario de brigada');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBrigUsersLoading(false);
    }
  };

  const resolveApkLogoUrl = () => {
    try {
      const fromWin = (window.__senerfiApkLogo || window.__apkLogoUrl || '').trim();
      const fromLS = (localStorage.getItem('senerfi_apk_logo') || '').trim();
      const pub = `${process.env.PUBLIC_URL || ''}/senerfiapk/logo.png`;
      return fromWin || fromLS || pub;
    } catch { return `${process.env.PUBLIC_URL || ''}/senerfiapk/logo.png`; }
  };

  const calendarLightTheme = {
    cellBg: '#FFFFFF',
    cellBgSelected: '#FFF7ED',
    cellBorder: '#E5E7EB',
    cellBorderSelected: '#FBBF24',
    dayText: '#1F2937',
  };

  // Removed local storage sync for brigades

  // Removed local storage sync for orders
  const takePhotoForStage = (stage) => { try { setCameraStage(stage); cameraFileRef.current && cameraFileRef.current.click(); } catch {} };
  useEffect(() => {
    if (!(mobileTab === 'camara' && mobileMode)) return;
    let id = null;
    try {
      id = navigator.geolocation && navigator.geolocation.watchPosition ? navigator.geolocation.watchPosition((p) => setCameraLoc({ lat: p.coords.latitude, lng: p.coords.longitude }), () => {}, { enableHighAccuracy: true }) : null;
    } catch {}
    return () => { try { if (id && navigator.geolocation && navigator.geolocation.clearWatch) navigator.geolocation.clearWatch(id); } catch {} };
  }, [mobileTab, mobileMode]);
  const renderNodeOverlay = async (src) => {
    try {
      const cv = cameraCanvasRef.current;
      if (!cv) return src;
      const w = 1080;
      const h = 1440;
      cv.width = w;
      cv.height = h;
      const ctx = cv.getContext('2d');
      ctx.fillStyle = '#000';
      ctx.fillRect(0,0,w,h);
      const base = new Image();
      base.crossOrigin = 'anonymous';
      await new Promise((res) => { base.onload = res; base.onerror = res; base.src = src; });
      const vw = base.naturalWidth || 720;
      const vh = base.naturalHeight || 1280;
      const scale = Math.min(w/vw, h/vh);
      const dw = vw*scale;
      const dh = vh*scale;
      const dx = (w-dw)/2;
      const dy = (h-dh)/2;
      ctx.drawImage(base, dx, dy, dw, dh);
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      const apkLogo = resolveApkLogoUrl();
      await new Promise((res) => { logo.onload = res; logo.onerror = () => { try { logo.src = senerfiBlanco; } catch {} res(); }; logo.src = apkLogo || senerfiBlanco; });
      ctx.globalAlpha = 0.9;
      const lw = 240;
      const lh = 80;
      ctx.drawImage(logo, 24, 24, lw, lh);
      ctx.globalAlpha = 1;
      const lat = cameraLoc?.lat;
      const lng = cameraLoc?.lng;
      const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || window.__gmapsKey || '';
      const sm = new Image();
      sm.crossOrigin = 'anonymous';
      const marker = lat && lng ? `markers=${lat},${lng}` : '';
      await new Promise((res) => { const url = key ? `https://maps.googleapis.com/maps/api/staticmap?size=480x240&zoom=15&${marker}&key=${key}` : ''; if (!url) return res(); sm.onload = res; sm.onerror = res; sm.src = url; });
      const bx = w - 24 - 480;
      const by = 24;
      if (sm.src) ctx.drawImage(sm, bx, by, 480, 240);
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(24, h-220, w-48, 196);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 40px system-ui, sans-serif';
      const ord = selectedCameraOrderId || '—';
      const brigName = (() => {
        try {
          const u = (brigUsers || []).find((x) => String(x.id) === String(mobileBrigId));
          return u?.nombre || '';
        } catch { return ''; }
      })();
      ctx.fillText(`Brigada: ${brigName || '—'}`, 40, h-196);
      const nodeLabel = `Nodo ${activeNodeIndex+1}`;
      ctx.fillText(`Orden: ${ord}`, 40, h-160);
      ctx.font = 'bold 32px system-ui, sans-serif';
      ctx.fillText(nodeLabel, 40, h-116);
      ctx.font = '28px system-ui, sans-serif';
      const stageLabel = cameraStage==='antes'?'Antes':cameraStage==='durante'?'Durante':'Después';
      ctx.fillText(`Etapa: ${stageLabel}`, 40, h-80);
      const now = new Date();
      const ts = new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(now);
      const locTxt = lat && lng ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : '—';
      ctx.fillText(`Geo: ${locTxt} · ${ts}`, 40, h-44);
      const url = cv.toDataURL('image/jpeg', 0.92);
      try {
        const isNative = isNativeRuntime({ honorForceWeb: false });
        if (isNative) {
          const base64 = url.split(',')[1] || '';
          const pad = (n) => String(n).padStart(2, '0');
          const stageLabel = cameraStage==='antes'?'ANTES':cameraStage==='durante'?'DURANTE':'DESPUES';
          const ord = selectedCameraOrderId || 'ORD';
          const nodeDir = `${ord}-NODO-${activeNodeIndex+1}`;
          const fname = `${ord}_${nodeDir}_${stageLabel}_${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.jpg`;
          await Filesystem.requestPermissions();
          await Filesystem.mkdir({ path: `Pictures/Senerfi/${ord}`, directory: Directory.ExternalStorage, recursive: true }).catch(() => {});
          await Filesystem.mkdir({ path: `Pictures/Senerfi/${ord}/${nodeDir}`, directory: Directory.ExternalStorage, recursive: true }).catch(() => {});
          await Filesystem.writeFile({ path: `Pictures/Senerfi/${ord}/${nodeDir}/${fname}`, data: base64, directory: Directory.ExternalStorage, encoding: Encoding.BASE64 });
        }
      } catch {}
      return url;
    } catch {
      return src;
    }
  };
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [delAuthEmail, setDelAuthEmail] = useState('');
  const [delAuthPass, setDelAuthPass] = useState('');
  const [delAuthMsg, setDelAuthMsg] = useState('');
  const [isReprogOpen, setIsReprogOpen] = useState(false);
  const [reprogOrderId, setReprogOrderId] = useState(null);
  const [reprogBrigId, setReprogBrigId] = useState('');
  const [reprogDate, setReprogDate] = useState('');
  const [reprogTime, setReprogTime] = useState('');
  const [reprogMsg, setReprogMsg] = useState('');
  const [employees, setEmployees] = useState([]);
  const [order, setOrder] = useState({
    brigadeId: '',
    responsibleId: '',
    date: '',
    time: '',
    status: 'Programado',
    place: '',
    clientSupervisor: '',
    lat: '',
    lng: '',
    zone: '',
    municipality: '',
    requestNumber: '',
    activityClassification: '',
    work: '',
    notes: '',
  });
  const [syncMsg, setSyncMsg] = useState('');
  const [syncPressed, setSyncPressed] = useState(false);
  const [allMaterials, setAllMaterials] = useState([]);
  const [technicianStock, setTechnicianStock] = useState(null);
  const [technicianLoading, setTechnicianLoading] = useState(false);
  const [technicianIdentifier, setTechnicianIdentifier] = useState('');

  const loadData = React.useCallback(async () => {
    if (!isOnline) return;
    try {
      setSyncMsg('Cargando...');
      const [brigsRes, ordersRes, configRes, matsRes] = await Promise.all([
        api.operational.listBrigades(),
        api.operational.listOrders({ limit: 500 }),
        api.operational.getConfig({ type: 'activities' }),
        api.materials.get({ limit: 5000 })
      ]);

      if (brigsRes && brigsRes.ok && Array.isArray(brigsRes.data)) {
        setBrigades(brigsRes.data);
      }
      if (ordersRes && ordersRes.ok && Array.isArray(ordersRes.data)) {
        setSegOrders(ordersRes.data);
      }
      if (configRes && configRes.ok && Array.isArray(configRes.data)) {
        setConfigActs(normalizeConfigActs(configRes.data));
      }
      if (matsRes && matsRes.ok && Array.isArray(matsRes.data)) {
        setAllMaterials(matsRes.data);
      }
      setSyncMsg('');
    } catch (e) {
      console.error(e);
      setSyncMsg('Error cargando datos');
    }
  }, [isOnline]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const [materialsSource, setMaterialsSource] = useState([]);
  useEffect(() => {
    try {
      const raw = localStorage.getItem('senerfi_materials');
      const list = raw ? JSON.parse(raw) : [];
      if (Array.isArray(list)) setMaterialsSource(list);
    } catch {}
  }, []);
  const warehouseId = useMemo(() => {
    return location === 'santander-de-quilichao' ? 'santander' : 'popayan';
  }, [location]);
  const availableMaterials = useMemo(() => {
    // 1. Si tenemos stock de técnico cargado (contexto de completar orden con responsable)
    if (technicianStock !== null) {
      const list = Array.isArray(technicianStock) ? technicianStock : [];
      return list.map((m) => ({
        ...m,
        stockByWarehouse: { [warehouseId]: Number(m.stock || 0) },
        status: 'Activo'
      }));
    }

    // 2. Fallback: Stock por bodega (contexto general)
    const list = Array.isArray(materialsSource) ? materialsSource : [];
    const byWh = (m) => {
      const sbw = m?.stockByWarehouse || {};
      return parseInt(sbw[warehouseId] || 0) > 0;
    };
    return list.filter((m) => byWh(m) && String(m.status || '').toLowerCase().includes('activo'));
  }, [materialsSource, warehouseId, technicianStock]);

  const syncMaterials = async () => {
    try {
      setSyncMsg('Sincronizando…');
      const res = await api.stockByWarehouse.get({ warehouse_id: warehouseId });
      const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      const byCode = Object.fromEntries(rows.map((r) => [String(r.code || r.material_code || ''), Number(r.stock || 0)]));
      const base = Array.isArray(materialsSource) ? [...materialsSource] : [];
      const next = base.map((m) => {
        const code = String(m.code || '');
        const stock = byCode[code];
        const sbw = { ...(m.stockByWarehouse || {}) };
        if (typeof stock !== 'undefined') sbw[warehouseId] = stock;
        return { ...m, stockByWarehouse: sbw };
      });
      // agregar materiales no presentes
      rows.forEach((r) => {
        const code = String(r.code || r.material_code || '');
        if (code && !next.some((m) => String(m.code || '') === code)) {
          const item = { id: code, code, name: r.name || '', status: 'Activo', stockByWarehouse: { [warehouseId]: Number(r.stock || 0) } };
          next.push(item);
        }
      });
      try { localStorage.setItem('senerfi_materials', JSON.stringify(next)); } catch {}
      setMaterialsSource(next);
      setSyncMsg('Sincronizado');
      setTimeout(() => setSyncMsg(''), 2000);
    } catch (e) {
      setSyncMsg('Error al sincronizar');
      setTimeout(() => setSyncMsg(''), 3000);
    }
  };

  const fetchTechnicianStock = async (identifier) => {
    try {
      setTechnicianLoading(true);
      setTechnicianStock(null);
      const res = await api.technicianStock.get(identifier);
      if (res && res.ok && Array.isArray(res.data)) {
        setTechnicianStock(res.data);
      } else {
        setTechnicianStock([]);
      }
    } catch (e) {
      console.error(e);
      setTechnicianStock([]);
    } finally {
      setTechnicianLoading(false);
    }
  };

  const backupCompleted = async (rec) => {
    try {
      const id = rec?.id || Date.now();
      const dt = new Date().toISOString();
      const lines = [];
      lines.push(`OrdenId: ${id}`);
      lines.push(`FechaBackup: ${dt}`);
      lines.push(`Sede: ${rec?.location || ''}`);
      lines.push(`Brigada: ${rec?.brigadeId || ''}`);
      lines.push(`Estado: ${rec?.status || ''}`);
      lines.push(`Lugar: ${rec?.place || ''}`);
      lines.push(`Lat: ${rec?.lat || ''}`);
      lines.push(`Lng: ${rec?.lng || ''}`);
      lines.push(`TrabajoRealizado: ${rec?.execution?.doneWork || rec?.doneWork || ''}`);
      lines.push(`Notas: ${rec?.execution?.notes || ''}`);
      const nodes = Array.isArray(rec?.execution?.nodes) ? rec.execution.nodes : [];
      nodes.forEach((n, i) => {
        lines.push(`Nodo[${i+1}]: ${n.name || ''}`);
        lines.push(`NodoObservaciones[${i+1}]: ${n.observations || ''}`);
        lines.push(`NodoNotas[${i+1}]: ${n.notes || ''}`);
        (n.activities || []).forEach((a) => lines.push(`NodoActividad[${i+1}]: ${a.id} (${a.minutes||0} min)`));
        (n.materials || []).forEach((m) => lines.push(`NodoMaterial[${i+1}]: ${(m.code||'')}:${(m.name||'')}:${(m.qty||'')}`));
      });
      const content = lines.join('\n');
      // Removed local storage backup
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orden_${id}.txt`;
      document.body.appendChild(a);
      void recordOperationsDownload({ fileType: 'txt', moduleKey: 'control-operativo', reportName: 'Orden de trabajo (texto)' });
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 0);
      const cap = window.Capacitor;
      const fs = cap?.Plugins?.Filesystem;
      if (fs && cap?.getPlatform && cap.getPlatform() !== 'web') {
        try {
          await fs.writeFile({ path: `Senerfi/ordenes/orden_${id}.txt`, data: content, directory: 'DOCUMENTS', encoding: 'utf8' });
        } catch {}
      }
    } catch {}
  };
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [completeForm, setCompleteForm] = useState({
    status: 'En progreso',
    place: '',
    lat: '',
    lng: '',
    startTime: '',
    endTime: '',
    doneWork: '',
    selectedActs: [],
    activityMinutes: {},
    extraWork: '',
    materials: [],
    evidences: [],
    notes: '',
    node: '',
    nodeObservations: '',
    nodeMaterials: [],
  });
  const [completeMsg, setCompleteMsg] = useState('');
  // currentUser ya llega de useUserPrefs() (línea de arriba) — antes se
  // recalculaba localmente leyendo localStorage directo (una sola vez al
  // montar, sin reactividad), lo que además chocaba en sintaxis con el
  // currentUser desestructurado del contexto tras 9155cac.

  useEffect(() => {
    (async () => {
      try {
        const res = await api.hr.employees.list({ limit: 500, order: 'desc' });
        const rows = Array.isArray(res?.data) ? res.data : [];
        setEmployees(rows);
      } catch {
        setEmployees([]);
      }
    })();
  }, []);

  // Removed local storage config loading

  useEffect(() => {
    const list = brigUsers || [];
    const rid = String((currentUser?.responsibleCedula || currentUser?.id || '').toString());
    const match = list.find((u) => String(u.cedula || u.usuario || '') === rid);
    if (match) {
      setMobileBrigId(String(match.id));
      return;
    }
    if (!mobileBrigId && list.length > 0) {
      setMobileBrigId(String(list[0].id));
    }
  }, [brigUsers, currentUser, mobileBrigId]);

  const mobileBrigUser = useMemo(() => {
    const list = Array.isArray(brigUsers) ? brigUsers : [];
    return list.find((u) => String(u.id) === String(mobileBrigId)) || null;
  }, [brigUsers, mobileBrigId]);

  const mobileProcessType = String(mobileBrigUser?.tipo_proceso || '').trim();

  const mobileOrders = useMemo(() => {
    const list = Array.isArray(segOrders) ? segOrders : [];
    const brigId = String(mobileBrigId || '');
    const proc = mobileProcessType;
    return list.filter((o) => {
      const okBrig = String(o.brigade_user_id || o.brigadaUserId || '') === brigId;
      if (!okBrig) return false;
      if (!proc) return true;
      const op = String(o.processType || o.process_type || '').trim();
      if (!op) return true;
      return op === proc;
    });
  }, [segOrders, mobileBrigId, mobileProcessType]);

  // Removed local storage brigades loading

  // Removed local storage brigades sync

  // Removed local storage initialization

  const activeEmployees = useMemo(() => {
    const list = (employees || []).filter((e) => String(e.status || '').toLowerCase().includes('activo'));
    const nameOf = (p) => (p.name || p.nombre_completo || '').trim().toLowerCase();
    const cedOf = (p) => String(p.cedula || p.documento_identidad || p.dni || p.documento || p.doc || '').trim();
    return list.sort((a, b) => {
      const na = nameOf(a);
      const nb = nameOf(b);
      const cmp = na.localeCompare(nb);
      return cmp !== 0 ? cmp : cedOf(a).localeCompare(cedOf(b));
    });
  }, [employees]);

  const technicianOptions = useMemo(() => {
    const norm = (v) => String(v ?? '').toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return activeEmployees.filter((p) => {
      const role = norm(p.role || p.cargo_rol || p.position);
      const area = norm(p.area || p.department || p.departamento);
      return role.includes('tecnic') || area.includes('tecnic');
    });
  }, [activeEmployees]);

  const brigUsersForProcess = useMemo(() => {
    const key = ['linea_viva', 'proyectos', 'gruas'].includes(coGroup) ? coGroup : '';
    if (!key) return Array.isArray(brigUsers) ? brigUsers : [];
    return (Array.isArray(brigUsers) ? brigUsers : []).filter((u) => String(u?.tipo_proceso || '').trim() === key);
  }, [brigUsers, coGroup]);

  const segOrdersForProcess = useMemo(() => {
    const key = ['linea_viva', 'proyectos', 'gruas'].includes(coGroup) ? coGroup : '';
    if (!key) return Array.isArray(segOrders) ? segOrders : [];
    return (Array.isArray(segOrders) ? segOrders : []).filter((o) => {
      const raw = String(o?.processType || o?.process_type || o?.tipo_proceso || o?.tipoProceso || '').trim();
      const mapped = raw || 'linea_viva';
      return mapped === key;
    });
  }, [segOrders, coGroup]);

  const saveEditBrigade = async (e) => {
    e.preventDefault();
    try {
      setSyncMsg('Guardando...');
      const payload = {
        id: editBrigId,
        name: editBrigForm.name || '',
        description: editBrigForm.description || '',
        responsible_id: editBrigForm.responsibleId || '',
        members: Array.isArray(editBrigForm.members) ? editBrigForm.members : [],
      };
      const res = await api.operational.saveBrigade(payload);
      if (res && res.ok) {
        await loadData();
        setIsEditBrigOpen(false);
        setEditBrigId('');
        setEditBrigForm({ name: '', description: '', responsibleId: '', members: [] });
      } else {
        setSyncMsg('Error al guardar');
      }
    } catch {
      setSyncMsg('Error al guardar');
    }
  };

  const toggleEditBrigMember = (memberId) => {
    setEditBrigForm((prev) => {
      const exists = (prev.members || []).includes(memberId);
      const nextMembers = exists ? prev.members.filter((id) => id !== memberId) : [...(prev.members || []), memberId];
      return { ...prev, members: nextMembers };
    });
  };

  const brigadeOptions = useMemo(() => {
    const processKey = ['linea_viva', 'proyectos', 'gruas', 'alumbrado_publico'].includes(coGroup) ? coGroup : '';
    const opts = [];
    const usedNames = new Set();
    // 1. Usuarios de brigada (prioridad)
    (brigUsers || []).filter((u) => {
      if (!processKey) return true;
      return String(u?.tipo_proceso || '').trim() === processKey;
    }).forEach((u) => {
      const name = (u.brigada || u.nombre || '').trim();
      const key = name.toUpperCase();
      if (!name) return;
      // Permitimos duplicados si son usuarios diferentes, pero el usuario pidió "solo la brigada"
      // Si hay múltiples usuarios en la misma brigada, mostraremos múltiples opciones con el mismo nombre?
      // Mejor mostramos unique name, y si seleccionamos, seleccionamos el PRIMER usuario que coincida?
      // No, el usuario dijo "las cuales ya deberian de estar creadas en usuario de brigada".
      // Vamos a listar cada usuario pero con el label de su brigada.
      // Si hay 2 usuarios en BRIGADA1, aparecerá "BRIGADA1" dos veces? Eso sería confuso.
      // Asumiremos 1 usuario activo por brigada o que el nombre de brigada es único.
      // Si queremos unificar:
      if (usedNames.has(key)) return; // Solo una opción por nombre de brigada (usando el primer usuario encontrado)
      
      opts.push({
        type: 'USER',
        id: u.id, // ID del usuario
        label: name,
        sortName: key,
        original: u
      });
      usedNames.add(key);
    });

    return opts.sort((a, b) => a.sortName.localeCompare(b.sortName));
  }, [brigUsers, coGroup]);

  // Valor computado para el select
  const currentBrigadeValue = (() => {
    if (order.brigadaUserId) return `USER-${order.brigadaUserId}`;
    if (order.brigadeId) {
      // Si tenemos brigadeId, verificamos si existe un usuario para esa brigada que debimos haber usado
      // Pero si el estado actual es legacy (solo brigadeId), buscamos si esa brigada está en nuestras opciones como USER o BRIGADE
      // Para mantener consistencia visual, intentamos mapear
      const b = brigades.find(x => String(x.id) === String(order.brigadeId));
      if (b) {
        const name = (b.name || '').toUpperCase().trim();
        const optUser = brigadeOptions.find(o => o.type === 'USER' && o.sortName === name);
        if (optUser) return `USER-${optUser.id}`; // Preferimos mostrar la opción de usuario si existe
        return `BRIGADE-${order.brigadeId}`;
      }
      return `BRIGADE-${order.brigadeId}`;
    }
    return '';
  })();

  const handleBrigadeSelect = (e) => {
    const val = e.target.value;
    if (!val) {
      setOrder({ ...order, brigadaUserId: '', brigadeId: '', responsibleId: '' });
      return;
    }
    const [type, id] = val.split('-');
    
    if (type === 'USER') {
      const u = brigUsers.find((x) => String(x.id) === String(id));
      const code = u?.brigada || '';
      // Buscar si existe objeto brigada legacy
      const brig = brigades.find((b) => String(b.name || '').toUpperCase().trim() === String(code).toUpperCase().trim());
      setOrder({
        ...order,
        brigadaUserId: id,
        brigadeId: brig ? brig.id : '',
        responsibleId: u?.cedula || u?.usuario || ''
      });
    } else {
      // type === 'BRIGADE'
      const b = brigades.find((x) => String(x.id) === String(id));
      setOrder({
        ...order,
        brigadaUserId: '', // No hay usuario app
        brigadeId: id,
        responsibleId: b?.responsibleId || ''
      });
    }
  };

  const saveOrder = async (e) => {
    e.preventDefault();
    if ((!order.brigadaUserId && !order.brigadeId) || !order.date) {
      setStatusMsg('Completa brigada (usuario) y fecha.');
      return;
    }
    try {
      setStatusMsg('Guardando...');
      let respId = order.responsibleId;
      let finalBrigadeUserId = order.brigadaUserId;

      // Intentar vincular usuario de brigada si no está explícito pero tenemos brigadeId
      if (!finalBrigadeUserId && order.brigadeId) {
        const b = (brigades || []).find((x) => String(x.id) === String(order.brigadeId));
        if (b) {
           const bName = (b.name || '').toUpperCase().trim();
           const uMatch = (brigUsers || []).find(u => (u.brigada || u.nombre || '').toUpperCase().trim() === bName);
           if (uMatch) finalBrigadeUserId = uMatch.id;
        }
      }
      
      // Priorizar usuario de brigada si existe
      if (finalBrigadeUserId) {
        const u = (brigUsers || []).find((x) => String(x.id) === String(finalBrigadeUserId));
        if (u) {
          respId = u.cedula || u.usuario || '';
        }
      }
      
      if (!respId && order.brigadeId) {
        const b = (brigades || []).find((x) => String(x.id) === String(order.brigadeId));
        respId = b?.responsibleId || '';
      }

      const location = order.location || order.place || '';
      
      // Asegurar que enviamos el NOMBRE de la brigada si no hay ID numérico válido,
      // o si queremos persistir el nombre visible.
      let payloadBrigadeId = order.brigadeId;
      if (finalBrigadeUserId) {
         const u = (brigUsers || []).find((x) => String(x.id) === String(finalBrigadeUserId));
         if (u && (!payloadBrigadeId || payloadBrigadeId === '0')) {
             payloadBrigadeId = u.brigada || u.nombre || '';
         }
      }

      const payload = {
        ...order,
        brigadeId: payloadBrigadeId,
        brigadaUserId: finalBrigadeUserId,
        processType: (['linea_viva', 'proyectos', 'gruas', 'alumbrado_publico'].includes(coGroup) ? coGroup : (order.processType || '')),
        responsibleId: respId,
        location
      };
      const res = await api.operational.saveOrder(payload);
      if (res && res.ok) {
        setStatusMsg('Orden guardada.');
        await loadData();
        setIsOrderOpen(false);
        setOrder({
          brigadeId: '',
          brigadaUserId: '',
          responsibleId: '',
          date: '',
          time: '',
          status: 'Programado',
          place: '',
          clientSupervisor: '',
          lat: '',
          lng: '',
          zone: '',
          municipality: '',
          requestNumber: '',
          activityClassification: '',
          work: '',
          notes: '',
        });
      }
    } catch (err) {
      console.error(err);
      const msg = err?.message || 'Error de conexión.';
      const clean = msg.replace(/^HTTP\s+\d+:\s*/i, '');
      setStatusMsg(`Error al guardar orden: ${clean}`);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const current = segOrders.find(o => o.id === orderId);
      if (!current) return;
      const payload = { ...current, status };
      const res = await api.operational.saveOrder(payload);
      if (res && res.ok) {
        await loadData();
      }
    } catch {}
  };

  const openComplete = (o) => {
    setEditingOrderId(o.id);
    
    // Reset technician stock
    setTechnicianStock(null);
    setTechnicianIdentifier('');
    
    // Determine identifier for technician stock
    let ident = o.responsibleId;
    
    // Try to resolve from brigade user
    if (!ident && (o.brigadaUserId || o.brigade_user_id)) {
        const uid = o.brigadaUserId || o.brigade_user_id;
        const u = (brigUsers || []).find(x => String(x.id) === String(uid));
        if (u) ident = u.cedula || u.usuario;
    }
    
    // Try to resolve from brigade legacy
    if (!ident && o.brigadeId) {
        const b = (brigades || []).find(x => String(x.id) === String(o.brigadeId));
        if (b) ident = b.responsibleId;
    }

    if (ident) {
        setTechnicianIdentifier(String(ident));
        fetchTechnicianStock(ident);
    }

    const nodesRaw = Array.isArray(o.execution?.nodes)
      ? o.execution.nodes
      : (o.execution?.node ? [o.execution.node] : []);
    const nodesNorm = (nodesRaw || []).map((n) => {
      const actsArr = Array.isArray(n.activities)
        ? n.activities
        : (Array.isArray(n.selectedActs)
            ? (n.selectedActs || []).map((id) => ({ id, minutes: (n.activityMinutes || {})[String(id)] || 0 }))
            : []);
      return {
        name: n.name || '',
        observations: n.observations || '',
        materials: Array.isArray(n.materials) ? n.materials : [],
        retiredMaterials: Array.isArray(n.retiredMaterials) ? n.retiredMaterials : [],
        selectedActs: actsArr.map((a) => a.id),
        activityMinutes: Object.fromEntries((actsArr || []).map((a) => [String(a.id), Number(a.minutes) || 0])),
        notes: n.notes || '',
      };
    });
    setCompleteForm({
      status: o.status && String(o.status).toLowerCase().includes('comple') ? 'Completado' : 'En progreso',
      place: o.place || '',
      lat: o.lat || '',
      lng: o.lng || '',
      startTime: o.startTime || '',
      endTime: o.endTime || '',
      doneWork: o.execution?.doneWork || o.doneWork || '',
      
      notes: o.execution?.notes || o.notes || '',
      nodes: nodesNorm,
    });
    setCompleteMsg('');
    setIsCompleteOpen(true);
  };

  const openDeleteOrder = (o) => {
    setDeleteTargetId(o.id);
    setDelAuthEmail('');
    setDelAuthPass('');
    setDelAuthMsg('');
    setIsDeleteOpen(true);
  };

  const confirmDeleteWithAuth = async () => {
    try {
      const isAdmin = String(currentUser?.role || '').toLowerCase() === 'administrador';
      if (!isAdmin) {
        const list = Array.isArray(users) ? users : [];
        const match = list.find((u) => String(u.email).toLowerCase() === String(delAuthEmail).toLowerCase() && String(u.password || '') === String(delAuthPass));
        if (!match || String(match.role || '').toLowerCase() !== 'administrador') {
          setDelAuthMsg('Autorización inválida. Se requiere usuario administrador.');
          return;
        }
      }
      setDelAuthMsg('Eliminando...');
      const res = await api.operational.deleteOrder({ id: deleteTargetId });
      if (res && res.ok) {
        await loadData();
        setIsDeleteOpen(false);
      } else {
        setDelAuthMsg('Error al eliminar.');
      }
    } catch {
      setDelAuthMsg('No se pudo eliminar.');
    }
  };

  const openReprogram = (o) => {
    setReprogOrderId(o.id);
    setReprogBrigId(String(o.brigadeId || ''));
    setReprogDate(String(o.date || ''));
    setReprogTime(String(o.time || ''));
    setReprogMsg('');
    setIsReprogOpen(true);
  };

  const confirmReprogram = async () => {
    if (!reprogOrderId || !reprogBrigId) {
      setReprogMsg('Selecciona una brigada.');
      return;
    }
    const b = (brigades || []).find((x) => String(x.id) === String(reprogBrigId));
    const respId = b?.responsibleId || '';
    
    try {
      setReprogMsg('Guardando...');
      const current = segOrders.find((o) => o.id === reprogOrderId);
      if (current) {
        const oldB = (brigades || []).find((x) => String(x.id) === String(current.brigadeId));
        const oldName = oldB?.name || '';
        const newName = b?.name || '';
        const stamp = (() => { try { return new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date()); } catch { return ''; } })();
        const note = `Reprogramado de ${oldName || '—'} a ${newName || '—'}${stamp ? ' — ' + stamp : ''}`;
        const nextNotes = (current.notes ? String(current.notes) + ' | ' : '') + note;
        
        const payload = { 
          ...current, 
          brigadeId: String(reprogBrigId), 
          responsibleId: String(respId), 
          date: reprogDate || current.date, 
          time: reprogTime || current.time, 
          status: 'Programado', 
          notes: nextNotes 
        };
        
        const res = await api.operational.saveOrder(payload);
        if (res && res.ok) {
          await loadData();
          setIsReprogOpen(false);
        } else {
          setReprogMsg('Error al guardar.');
        }
      }
    } catch {
      setReprogMsg('Error al guardar.');
    }
  };

  const getBrigadeChipColor = (str) => {
    if (!str) return '#e5e7eb';
    const colors = [
      '#fca5a5', '#fdba74', '#fcd34d', '#86efac', '#6ee7b7',
      '#5eead4', '#67e8f9', '#7dd3fc', '#93c5fd', '#a5b4fc',
      '#c4b5fd', '#d8b4fe', '#f0abfc', '#fda4af'
    ];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getBrigadeLabel = (o) => {
    // 1. Intentar resolver por ID de usuario de brigada (prioridad)
    const buId = o.brigade_user_id || o.brigadaUserId;
    if (buId && String(buId) !== '0') {
      const u = (brigUsers || []).find((x) => String(x.id) === String(buId));
      if (u) {
        const code = u.brigada || '';
        const respName = u.nombre || '';
        const respId = u.cedula || u.usuario || '';
        const brigadeText = code || 'Sin brigada';
        const respText = respName ? `${respName}${respId ? ' (' + respId + ')' : ''}` : respId || '—';
        return `Brigada: ${brigadeText} · Responsable: ${respText}`;
      }
      // Si tenemos ID pero no encontramos el usuario en la lista cargada, mostramos el ID
      return `Brigada: ${o.brigadeId ? `Brigada ${o.brigadeId}` : '—'} · Resp: (Usuario ID ${buId})`;
    }

    // 2. Fallback: usar responsibleId de la orden si existe, o el de la brigada (legacy)
    const b = (brigades || []).find((x) => String(x.id) === String(o.brigadeId));
    // Priorizamos o.responsibleId porque es el guardado en la orden
    const respId = o.responsibleId || b?.responsibleId || '';
    
    const emp = (employees || []).find((e) => {
      const rid = String(respId);
      return String(e.id || '') === rid || String(e.cedula || e.documento_identidad || e.dni || e.documento || e.doc || '') === rid;
    });
    const respName = emp?.name || emp?.nombre_completo || '';
    return `Brigada: ${b?.name || '—'} · Responsable: ${respName ? respName + ' (' + respId + ')' : respId || '—'}`;
  };

  const saveComplete = async (e) => {
    e.preventDefault();
    if (String(completeForm.status || '').toLowerCase().includes('comple')) {
      if (!completeForm.startTime || !completeForm.endTime) {
        setCompleteMsg('Completa hora inicio y hora fin para estado Completado.');
        return;
      }
      const current = segOrders.find((x) => x.id === editingOrderId) || {};
      const nodesListCheck = Array.isArray(completeForm.nodes) ? completeForm.nodes : [];
      const hasNodeWorkCheck = nodesListCheck.some((n) => (n.selectedActs || []).length > 0 || (n.materials || []).length > 0 || String(n.observations || '').trim() || String(n.notes || '').trim());
      const hasWork = Boolean(String(completeForm.doneWork || '').trim()) || hasNodeWorkCheck;
      if (!hasWork) {
        setCompleteMsg('Registra actividades por nodo o trabajo realizado.');
        return;
      }
      const cls = String(current.activityClassification || '').toLowerCase();
      if (cls.includes('emerg')) {
        if (!hasNodeWorkCheck) {
          setCompleteMsg('Registra actividades/materiales/observaciones en al menos un nodo para Emergencia.');
          return;
        }
      }
    }
    let savedRec = null;
    try {
      setCompleteMsg('Guardando...');
      const o = segOrders.find((x) => x.id === editingOrderId);
      if (o) {
        const finalWork = [completeForm.doneWork || '']
          .map((s) => String(s || '').trim())
          .filter(Boolean)
          .join('\n');
        
        const nodesList = Array.isArray(completeForm.nodes) ? completeForm.nodes : [];
        const hasNodeWork = nodesList.some((n) => (n.selectedActs || []).length > 0 || (n.materials || []).length > 0 || String(n.observations || '').trim());
        const nodesOut = nodesList
          .map((n) => {
            const sel = Array.isArray(n.selectedActs) ? n.selectedActs : [];
            const mMap = Object.fromEntries(
              Object.entries(n.activityMinutes || {}).map(([k, v]) => [k, Number.isFinite(Number(v)) ? Number(v) : 0])
            );
            const actsOut = sel.map((id) => ({ id, minutes: mMap[String(id)] || 0 }));
            return {
              name: n.name || '',
              observations: n.observations || '',
              materials: Array.isArray(n.materials) ? n.materials : [],
              retiredMaterials: Array.isArray(n.retiredMaterials) ? n.retiredMaterials : [],
              activities: actsOut,
              notes: n.notes || '',
            };
          })
          .filter((n) => String(n.name || '').trim() || (n.materials || []).length || (n.activities || []).length || String(n.observations || '').trim());
          
        if (String(completeForm.status || '').toLowerCase().includes('comple')) {
          const hasWork = Boolean(finalWork) || hasNodeWork;
          if (!hasWork) {
            setCompleteMsg('Registra actividades por nodo o notas de trabajo.');
            return;
          }
        }
        
        const execution = {
          status: completeForm.status,
          startTime: completeForm.startTime,
          endTime: completeForm.endTime,
          doneWork: finalWork,
          notes: completeForm.notes || '',
          nodes: nodesOut,
        };
        
        const payload = {
          ...o,
          status: completeForm.status,
          place: completeForm.place || o.place,
          lat: completeForm.lat || o.lat,
          lng: completeForm.lng || o.lng,
          time: completeForm.startTime || o.time, // Guardar hora inicio como hora principal si aplica
          execution,
        };

        const res = await api.operational.saveOrder(payload);
        if (res && res.ok) {
          await loadData();
          savedRec = { ...payload, ...res.data };
          if (savedRec && String(savedRec.status || '').toLowerCase().includes('comple')) {
            backupCompleted({ ...savedRec, location });
          }
          setIsCompleteOpen(false);
          setCompleteMsg('');
          setEditingOrderId(null);
        } else {
          setCompleteMsg('Error al guardar.');
        }
      }
    } catch {
      setCompleteMsg('Error al guardar.');
    }
  };

  const addConfigAct = async () => {
    const n = String(actForm.name || '').trim();
    const d = String(actForm.description || '').trim();
    const pt = String(actForm.process_type || '').trim();
    const vuRaw = String(actForm.valor_unitario ?? '').trim();
    const vu = vuRaw === '' ? '' : (Number.isFinite(Number(vuRaw)) ? Number(vuRaw) : vuRaw);
    if (!n) return;
    const itemId = actEditId ? actEditId : Date.now();
    const item = { id: itemId, name: n, description: d, process_type: pt, valor_unitario: vu };
    const base = normalizeConfigActs(configActs);
    const next = actEditId
      ? (base.some((x) => String(x.id) === String(itemId))
          ? base.map((x) => (String(x.id) === String(itemId) ? item : x))
          : [item, ...base])
      : [item, ...base];
    try {
      const res = await api.operational.saveConfig({ type: 'activities', data: next });
      if (res && res.ok) {
        setConfigActs(next);
        setActForm({ name: '', description: '', process_type: 'linea_viva', valor_unitario: '' });
        setActEditId('');
      }
    } catch {}
  };

  const removeConfigAct = async (id) => {
    const base = normalizeConfigActs(configActs);
    const next = base.filter((x) => String(x.id) !== String(id));
    try {
      const res = await api.operational.saveConfig({ type: 'activities', data: next });
      if (res && res.ok) {
        setConfigActs(next);
        if (String(actEditId) === String(id)) {
          setActEditId('');
          setActForm({ name: '', description: '', process_type: 'linea_viva', valor_unitario: '' });
        }
      }
    } catch {}
  };

  const [segQuery, setSegQuery] = useState('');
  const [segStatus, setSegStatus] = useState('Todos');
  const [segBrigId, setSegBrigId] = useState('');
  const [segSelectedDay, setSegSelectedDay] = useState(null);
  const [segViewDate, setSegViewDate] = useState(() => new Date());
  const segYear = segViewDate.getFullYear();
  const segMonth = segViewDate.getMonth();
  const segMonthLabel = new Intl.DateTimeFormat('es-CO', { month: 'long', year: 'numeric' }).format(segViewDate);
  // Removed local storage sync logic
  const segFiltered = useMemo(() => {
    const norm = (s) => String(s || '').toLowerCase();
    const mapStatus = (s) => {
      const v = norm(s);
      if (v.includes('curso') || v.includes('progreso')) return 'en progreso';
      if (v.includes('pend')) return 'pendiente';
      if (v.includes('comple')) return 'completado';
      if (v.includes('cancel')) return 'cancelado';
      return v;
    };
    return segOrdersForProcess.filter((o) => {
      const q = norm(segQuery);
      const brig = (brigades || []).find((x) => String(x.id) === String(o.brigadeId));
      const u = (brigUsersForProcess || []).find((x) => String(x.id) === String(o.brigade_user_id || o.brigadaUserId || ''));
      const matchQ = q
        ? (
            norm(o.place).includes(q) ||
            norm(o.work).includes(q) ||
            String(o.responsibleId).includes(q) ||
            norm(brig?.name).includes(q) ||
            norm(u?.nombre).includes(q)
          )
        : true;
      const st = mapStatus(o.status);
      const matchSt = segStatus === 'Todos' ? true : st === norm(segStatus);
      const matchBrig = segBrigId ? String(o.brigade_user_id || o.brigadaUserId || '') === String(segBrigId) : true;
      const d = new Date(o.date);
      return matchQ && matchSt && matchBrig && d.getFullYear() === segYear && d.getMonth() === segMonth;
    });
  }, [segOrdersForProcess, segQuery, segStatus, segBrigId, brigades, brigUsersForProcess, segYear, segMonth]);
  const segDaysInMonth = new Date(segYear, segMonth + 1, 0).getDate();
  const segStartWeekday = new Date(segYear, segMonth, 1).getDay();
  const segCalendarCells = useMemo(() => {
    const cells = [];
    for (let i = 0; i < segStartWeekday; i++) cells.push(null);
    for (let d = 1; d <= segDaysInMonth; d++) {
      const count = segFiltered.filter((o) => new Date(o.date).getDate() === d).length;
      cells.push({ day: d, count });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [segStartWeekday, segDaysInMonth, segFiltered]);
  const gotoPrevMonth = () => setSegViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const gotoNextMonth = () => setSegViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const segDayList = useMemo(() => {
    if (!segSelectedDay) return [];
    return segFiltered.filter((o) => {
      const d = new Date(o.date);
      return d.getFullYear() === segYear && d.getMonth() === segMonth && d.getDate() === segSelectedDay;
    });
  }, [segFiltered, segSelectedDay, segYear, segMonth]);
  const segSummary = useMemo(() => {
    const out = { pendiente: 0, 'en progreso': 0, completado: 0, cancelado: 0 };
    segFiltered.forEach((o) => {
      const v = String(o.status || '').toLowerCase();
      let k = 'pendiente';
      if (v.includes('curso') || v.includes('progreso')) k = 'en progreso';
      else if (v.includes('comple')) k = 'completado';
      else if (v.includes('cancel')) k = 'cancelado';
      else if (v.includes('pend')) k = 'pendiente';
      out[k] += 1;
    });
    return out;
  }, [segFiltered]);

  const [incQuery, setIncQuery] = useState('');
  const [incStatus, setIncStatus] = useState('Programado');
  const [incBrigId, setIncBrigId] = useState('');
  const incList = useMemo(() => {
    const norm = (s) => String(s || '').toLowerCase();
    const mapStatus = (s) => {
      const v = norm(s);
      if (v.includes('program')) return 'programado';
      if (v.includes('curso') || v.includes('progreso')) return 'en progreso';
      if (v.includes('pend')) return 'pendiente';
      if (v.includes('comple')) return 'completado';
      if (v.includes('cancel')) return 'cancelado';
      return v;
    };
    const stFilter = norm(incStatus);
    const filtered = segOrdersForProcess.filter((o) => {
      const q = norm(incQuery);
      const brig = (brigades || []).find((x) => String(x.id) === String(o.brigadeId));
      const u = (brigUsersForProcess || []).find((x) => String(x.id) === String(o.brigade_user_id || o.brigadaUserId || ''));
      const matchQ = q
        ? (
            norm(o.place).includes(q) ||
            norm(o.work).includes(q) ||
            String(o.responsibleId).includes(q) ||
            norm(brig?.name).includes(q) ||
            norm(u?.nombre).includes(q)
          )
        : true;
      const st = mapStatus(o.status);
      const matchSt = stFilter === 'todos' ? true : st === stFilter;
      const matchBrig = incBrigId ? String(o.brigade_user_id || o.brigadaUserId || '') === String(incBrigId) : true;
      return matchQ && matchSt && matchBrig;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
    return filtered;
  }, [segOrdersForProcess, incQuery, incStatus, incBrigId, brigades, brigUsersForProcess]);

  const [repStatus, setRepStatus] = useState('Programado');
  const [repLocation, setRepLocation] = useState('Actual');
  const [repBrigId, setRepBrigId] = useState('');
  const [repStartDate, setRepStartDate] = useState('');
  const [repEndDate, setRepEndDate] = useState('');
  const filteredOrdersForReports = useMemo(() => {
    const norm = (s) => String(s || '').toLowerCase();
    const st = norm(repStatus);
    return segOrdersForProcess.filter((o) => {
      const okLoc = repLocation === 'Todas' ? true : String(o.location || '') === String(location || '');
      const v = norm(o.status);
      const mapped = v.includes('program') ? 'programado' : v.includes('curso') || v.includes('progreso') ? 'en progreso' : v.includes('comple') ? 'completado' : v.includes('cancel') ? 'cancelado' : v.includes('pend') ? 'pendiente' : v;
      const okSt = st === 'todos' ? true : mapped === st;
      const okBrig = repBrigId ? String(o.brigade_user_id || o.brigadaUserId || '') === String(repBrigId) : true;
      const d = new Date(o.date);
      const okStart = repStartDate ? d >= new Date(repStartDate) : true;
      const okEnd = repEndDate ? d <= new Date(repEndDate) : true;
      return okLoc && okSt && okBrig && okStart && okEnd;
    });
  }, [segOrdersForProcess, repStatus, repLocation, repBrigId, repStartDate, repEndDate, location]);
  const exportReport = () => {
    const source = repBrigId ? repBrigades.filter((x) => String(x.b.id) === String(repBrigId)) : repBrigades;
    const rows = source.map(({ b, count, respName, respId }) => ({
      Brigada: b.name || '',
      Responsable: respName || '',
      Cedula: respId || '',
      Ordenes: count,
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'GestionBrigadas');
    const start = repStartDate || '';
    const end = repEndDate || '';
    const rangeLabel = start && end ? `${start}_${end}` : start ? `desde_${start}` : end ? `hasta_${end}` : '';
    const file = rangeLabel
      ? (repBrigId ? `gestion_brigadas_${repBrigId}_${rangeLabel}.xlsx` : `gestion_brigadas_${rangeLabel}.xlsx`)
      : (repBrigId ? `gestion_brigadas_${repBrigId}.xlsx` : 'gestion_brigadas.xlsx');
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'control-operativo', reportName: 'Gestión de brigadas' });
    XLSX.writeFile(wb, file);
  };
  const exportExecutionReport = () => {
    const rows = filteredOrdersForReports.map((o) => {
      // Resolución de Brigada y Responsable
      let brigadeName = '';
      let respName = '';
      let respId = '';

      const buId = o.brigade_user_id || o.brigadaUserId;
      if (buId && String(buId) !== '0') {
         const u = (brigUsers || []).find((x) => String(x.id) === String(buId));
         if (u) {
             brigadeName = u.brigada || u.nombre || '';
             respName = u.nombre || '';
             respId = u.cedula || u.usuario || '';
         } else {
             brigadeName = `Usuario ID ${buId}`;
         }
      } else {
         // Fallback: Si brigadeId es texto (nombre directo) o ID legacy
         if (o.brigadeId && isNaN(Number(o.brigadeId))) {
             brigadeName = o.brigadeId;
         } else {
             const b = (brigades || []).find((x) => String(x.id) === String(o.brigadeId));
             brigadeName = b?.name || o.brigadeId || '';
         }
         respId = o.responsibleId || '';
         const emp = (employees || []).find(e => String(e.id) === String(respId) || String(e.cedula || e.documento || e.doc) === String(respId));
         respName = emp?.name || emp?.nombre_completo || '';
      }
      
      const acts = (o.execution?.selectedActs || []).map((id) => {
        const a = configActs.find((x) => String(x.id) === String(id));
        const min = (o.execution?.activityMinutes || {})[String(id)] || 0;
        return `${a?.name || id} (${min} min)`;
      }).filter(Boolean);
      const mats = (o.execution?.materials || []).map((m) => `${m.name || ''}:${m.qty || ''}`).filter(Boolean);
      
      const nodesArr = Array.isArray(o.execution?.nodes) ? o.execution.nodes : (o.execution?.node ? [o.execution.node] : []);
      const nNames = nodesArr.map((n) => n.name || '').filter(Boolean);
      const nObsAll = nodesArr.map((n) => n.observations || '').filter(Boolean);
      const nNotesAll = nodesArr.map((n) => n.notes || '').filter(Boolean);
      const nActsAll = nodesArr.flatMap((n) => {
        const actsArr = Array.isArray(n.activities)
          ? n.activities
          : (Array.isArray(n.selectedActs)
              ? (n.selectedActs || []).map((id) => ({ id, minutes: (n.activityMinutes || {})[String(id)] || 0 }))
              : []);
        return actsArr.map(({ id, minutes }) => {
          const a = configActs.find((x) => String(x.id) === String(id));
          const mm = Number(minutes) || 0;
          return `${n.name ? '[' + n.name + '] ' : ''}${a?.name || id} (${mm} min)`;
        });
      }).filter(Boolean);
      const nMatsAll = nodesArr.flatMap((n) => (n.materials || []).map((m) => `${n.name ? '[' + n.name + '] ' : ''}${m.name || ''}:${m.qty || ''}`)).filter(Boolean);
      const totalNodeMinutes = nodesArr.reduce((sum, n) => sum + (Array.isArray(n.activities) ? n.activities.reduce((s, x) => s + (Number(x.minutes) || 0), 0) : 0), 0);
      
      return {
        OrdenId: o.id,
        Sede: o.location || '',
        Brigada: brigadeName,
        ResponsableNombre: respName,
        ResponsableID: respId,
        SupervisorCliente: o.clientSupervisor || '',
        Fecha: o.date || '',
        Hora: o.time || '',
        Estado: o.status || '',
        Lugar: o.place || '',
        Latitud: o.lat || '',
        Longitud: o.lng || '',
        Zona: o.zone || '',
        Municipio: o.municipality || '',
        Solicitud: o.requestNumber || '',
        Clasificacion: o.activityClassification || '',
        TrabajoProgramado: o.work || '',
        NotasIniciales: o.notes || '',
        
        ExecEstado: o.execution?.status || '',
        HoraInicio: o.execution?.startTime || o.startTime || '',
        HoraFin: o.execution?.endTime || o.endTime || '',
        TrabajoRealizado: o.execution?.doneWork || o.doneWork || '',
        ObservacionesCierre: o.execution?.notes || '',
        
        ActividadesGenerales: acts.join('; '),
        MaterialesGenerales: mats.join('; '),
        
        NodosNombres: nNames.join(' | '),
        NodosObservaciones: nObsAll.join(' | '),
        NodosNotas: nNotesAll.join(' | '),
        NodosMateriales: nMatsAll.join(' | '),
        NodosActividades: nActsAll.join(' | '),
        MinutosTotales: totalNodeMinutes,
      };
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Ejecucion');
    const start = repStartDate || '';
    const end = repEndDate || '';
    const rangeLabel = start && end ? `${start}_${end}` : start ? `desde_${start}` : end ? `hasta_${end}` : '';
    const file = rangeLabel
      ? (repBrigId ? `ejecucion_${repBrigId}_${rangeLabel}.xlsx` : `ejecucion_${rangeLabel}.xlsx`)
      : (repBrigId ? `ejecucion_${repBrigId}.xlsx` : 'ejecucion.xlsx');
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'control-operativo', reportName: 'Reporte de ejecución' });
    XLSX.writeFile(wb, file);
  };
  const repTotal = useMemo(() => filteredOrdersForReports.length, [filteredOrdersForReports]);

  const repBrigades = useMemo(() => {
    const pool = repBrigId ? (brigUsersForProcess || []).filter((u) => String(u.id) === String(repBrigId)) : (brigUsersForProcess || []);
    const list = pool.map((u) => {
      const count = filteredOrdersForReports.filter((o) => String(o.brigade_user_id || o.brigadaUserId || '') === String(u.id)).length;
      return { 
        b: { id: u.id, name: u.brigada || u.nombre }, 
        count, 
        respName: u.nombre, 
        respId: u.cedula 
      };
    });
    return list.sort((a, b) => b.count - a.count);
  }, [brigUsersForProcess, filteredOrdersForReports, repBrigId]);

  const loadGMap = (key) => {
    if (window.google && window.google.maps) return Promise.resolve(window.google.maps);
    if (!key) return Promise.reject(new Error('no-key'));
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `https://maps.googleapis.com/maps/api/js?key=${key}`;
      s.async = true;
      s.onload = () => resolve(window.google.maps);
      s.onerror = reject;
      document.head.appendChild(s);
    });
  };

  const MapView = ({ items }) => {
    const ref = React.useRef(null);
    const [ready, setReady] = useState(false);
    useEffect(() => {
      const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || window.__gmapsKey || '';
      const run = async () => {
        try {
          const gm = await loadGMap(key);
          const valid = (items || []).filter((o) => {
            const lat = parseFloat(o.lat);
            const lng = parseFloat(o.lng);
            return isFinite(lat) && isFinite(lng);
          });
          const first = valid[0];
          const center = first ? { lat: parseFloat(first.lat), lng: parseFloat(first.lng) } : { lat: 4.711, lng: -74.072 };
          const map = new gm.Map(ref.current, { center, zoom: 12 });
          valid.forEach((o) => {
            const lat = parseFloat(o.lat);
            const lng = parseFloat(o.lng);
            const m = new gm.Marker({ position: { lat, lng }, map, title: o.place || o.work || '' });
            const title = (o.work || o.place || '').toString();
            const when = `${o.date || ''} ${o.time || ''}`.trim();
            const muni = o.municipality || '';
            const zone = o.zone || '';
            const content = `
              <div style="font-family:system-ui,sans-serif;font-size:12px;color:#374151">
                <div style="font-weight:600;color:#776F74">${title}</div>
                ${when ? `<div><span style="color:#6b7280">Fecha/Hora:</span> ${when}</div>` : ''}
                ${muni ? `<div><span style="color:#6b7280">Municipio:</span> ${muni}</div>` : ''}
                ${zone ? `<div><span style="color:#6b7280">Zona:</span> ${zone}</div>` : ''}
              </div>
            `;
            const inf = new gm.InfoWindow({ content });
            m.addListener('click', () => inf.open(map, m));
          });
          setReady(true);
        } catch {
          setReady(false);
        }
      };
      run();
    }, [items]);
    return (
      <div
        className="rounded-2xl border-2 shadow-2xl"
        ref={ref}
        style={{ height: '50vh', borderColor: '#776F74', boxShadow: '0 12px 28px rgba(0,0,0,0.18), 0 8px 14px rgba(119,111,116,0.15), 0 0 0 2px #776F74' }}
      >
        {!ready && (<div className="text-xs p-2" style={{ color: '#776F74' }}>Cargando mapa</div>)}
      </div>

      
    );
  };

  const OSMEmbed = ({ center, markers = [] }) => {
    const list = Array.isArray(markers) ? markers.slice(0, 10) : [];
    const cLat = parseFloat(center?.lat);
    const cLng = parseFloat(center?.lng);
    const hasCenter = Number.isFinite(cLat) && Number.isFinite(cLng);
    const points = hasCenter ? [{ lat: cLat, lng: cLng }, ...list] : list;
    const mins = points.reduce((acc, p) => ({
      minLat: Math.min(acc.minLat, parseFloat(p.lat)),
      minLng: Math.min(acc.minLng, parseFloat(p.lng)),
      maxLat: Math.max(acc.maxLat, parseFloat(p.lat)),
      maxLng: Math.max(acc.maxLng, parseFloat(p.lng)),
    }), { minLat: 4.660989, minLng: -74.12209, maxLat: 4.760989, maxLng: -74.02209 });
    const padLat = 0.05; const padLng = 0.05;
    const bbox = `${mins.minLng - padLng},${mins.minLat - padLat},${mins.maxLng + padLng},${mins.maxLat + padLat}`;
    const markersParam = list.map((m) => `&marker=${parseFloat(m.lat)},${parseFloat(m.lng)}`).join('');
    const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik${markersParam}`;
    return (
      <iframe title="Mapa" src={src} className="w-full rounded-2xl border" style={{ height: '50vh', borderColor: '#E5E7EB' }} />
    );
  };

  const CameraTab = ({ brigadeName, cedula }) => {
    const vref = React.useRef(null);
    const cref = React.useRef(null);
    const fref = React.useRef(null);
    const isNative = isNativeRuntime({ honorForceWeb: false });
    const [, setStreamOk] = useState(false);
    const [loc, setLoc] = useState(null);
    const [shots, setShots] = useState([]);
    const [showGallery, setShowGallery] = useState(false);
    useEffect(() => {
      let s;
      if (!isNative && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        (async () => {
          try {
            s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
            if (vref.current) vref.current.srcObject = s;
            setStreamOk(true);
          } catch {
            setStreamOk(false);
          }
        })();
      }
      let id = null;
      try {
        id = navigator.geolocation && navigator.geolocation.watchPosition ? navigator.geolocation.watchPosition((p) => setLoc({ lat: p.coords.latitude, lng: p.coords.longitude }), () => {}, { enableHighAccuracy: true }) : null;
      } catch {}
      return () => {
        try { if (id && navigator.geolocation && navigator.geolocation.clearWatch) navigator.geolocation.clearWatch(id); } catch {}
        try { (s && s.getTracks ? s.getTracks() : []).forEach(t => t.stop()); } catch {}
      };
    }, [isNative]);
    const processToCanvas = async (imgSrc) => {
      try {
        const cv = cref.current;
        if (!cv) return;
        const w = 1080;
        const h = 1440;
        cv.width = w;
        cv.height = h;
        const ctx = cv.getContext('2d');
        ctx.fillStyle = '#000';
        ctx.fillRect(0,0,w,h);
        const srcImg = new Image();
        srcImg.crossOrigin = 'anonymous';
        await new Promise((res) => { srcImg.onload = res; srcImg.onerror = res; srcImg.src = imgSrc; });
        const vw = srcImg.naturalWidth || 720;
        const vh = srcImg.naturalHeight || 1280;
        const scale = Math.min(w/vw, h/vh);
        const dw = vw*scale;
        const dh = vh*scale;
        const dx = (w-dw)/2;
        const dy = (h-dh)/2;
        ctx.drawImage(srcImg, dx, dy, dw, dh);
        const logo = new Image();
        logo.crossOrigin = 'anonymous';
        const apkLogo = resolveApkLogoUrl();
        await new Promise((res) => { logo.onload = res; logo.onerror = () => { try { logo.src = senerfiBlanco; } catch {} res(); }; logo.src = apkLogo || senerfiBlanco; });
        ctx.globalAlpha = 0.9;
        const lw = 240;
        const lh = 80;
        ctx.drawImage(logo, 24, 24, lw, lh);
        ctx.globalAlpha = 1;
        const lat = loc?.lat;
        const lng = loc?.lng;
        const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || window.__gmapsKey || '';
        const sm = new Image();
        sm.crossOrigin = 'anonymous';
        const marker = lat && lng ? `markers=${lat},${lng}` : '';
        await new Promise((res) => { const url = key ? `https://maps.googleapis.com/maps/api/staticmap?size=480x240&zoom=15&${marker}&key=${key}` : ''; if (!url) return res(); sm.onload = res; sm.onerror = res; sm.src = url; });
        const bx = w - 24 - 480;
        const by = 24;
        if (sm.src) ctx.drawImage(sm, bx, by, 480, 240);
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(24, h-200, w-48, 176);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 40px system-ui, sans-serif';
        ctx.fillText(`Brigada: ${brigadeName || '—'}`, 40, h-140);
        ctx.font = '28px system-ui, sans-serif';
        ctx.fillText(`Cédula: ${cedula || '—'}`, 40, h-96);
        const now = new Date();
        const ts = new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(now);
        const locTxt = lat && lng ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : '—';
        ctx.fillText(`Geo: ${locTxt} · ${ts}`, 40, h-52);
        const url = cv.toDataURL('image/jpeg', 0.92);
        setShots((prev) => [url, ...prev].slice(0, 12));
        try {
          if (isNative) {
            const base64 = url.split(',')[1] || '';
            const now = new Date();
            const pad = (n) => String(n).padStart(2, '0');
            const fname = `senerfi_${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.jpg`;
            await Filesystem.requestPermissions();
            await Filesystem.mkdir({ path: 'Pictures/Senerfi', directory: Directory.ExternalStorage, recursive: true }).catch(() => {});
            await Filesystem.writeFile({ path: `Pictures/Senerfi/${fname}`, data: base64, directory: Directory.ExternalStorage, encoding: Encoding.BASE64 });
          }
        } catch {}
      } catch {}
    };
    const capture = async () => {
      try {
        const cv = cref.current;
        const v = vref.current;
        if (!cv || !v) return;
        const w = 1080;
        const h = 1440;
        cv.width = w;
        cv.height = h;
        const ctx = cv.getContext('2d');
        ctx.fillStyle = '#000';
        ctx.fillRect(0,0,w,h);
        const vw = v.videoWidth || 720;
        const vh = v.videoHeight || 1280;
        const scale = Math.min(w/vw, h/vh);
        const dw = vw*scale;
        const dh = vh*scale;
        const dx = (w-dw)/2;
        const dy = (h-dh)/2;
        ctx.drawImage(v, dx, dy, dw, dh);
        const logo = new Image();
        logo.crossOrigin = 'anonymous';
        const apkLogo = resolveApkLogoUrl();
        await new Promise((res) => { logo.onload = res; logo.onerror = () => { try { logo.src = senerfiBlanco; } catch {} res(); }; logo.src = apkLogo || senerfiBlanco; });
        ctx.globalAlpha = 0.9;
        const lw = 240;
        const lh = 80;
        ctx.drawImage(logo, 24, 24, lw, lh);
        ctx.globalAlpha = 1;
        const lat = loc?.lat;
        const lng = loc?.lng;
        const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || window.__gmapsKey || '';
        const sm = new Image();
        sm.crossOrigin = 'anonymous';
        const marker = lat && lng ? `markers=${lat},${lng}` : '';
        await new Promise((res) => { const url = key ? `https://maps.googleapis.com/maps/api/staticmap?size=480x240&zoom=15&${marker}&key=${key}` : ''; if (!url) return res(); sm.onload = res; sm.onerror = res; sm.src = url; });
        const bx = w - 24 - 480;
        const by = 24;
        if (sm.src) ctx.drawImage(sm, bx, by, 480, 240);
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(24, h-200, w-48, 176);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 40px system-ui, sans-serif';
        ctx.fillText(`Brigada: ${brigadeName || '—'}`, 40, h-140);
        ctx.font = '28px system-ui, sans-serif';
        ctx.fillText(`Cédula: ${cedula || '—'}`, 40, h-96);
        const now = new Date();
        const ts = new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(now);
        const locTxt = lat && lng ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : '—';
        ctx.fillText(`Geo: ${locTxt} · ${ts}`, 40, h-52);
        const url = cv.toDataURL('image/jpeg', 0.92);
        setShots((prev) => [url, ...prev].slice(0, 12));
      } catch {}
    };
    const onNativeFile = async (e) => {
      try {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (ev) => {
          const dataUrl = ev.target?.result;
          if (typeof dataUrl === 'string') await processToCanvas(dataUrl);
        };
        reader.readAsDataURL(file);
      } catch {}
    };
    const deleteShot = (i) => {
      setShots((prev) => prev.filter((_, idx) => idx !== i));
    };
    return (
      <div>
        <div className="text-lg font-semibold mb-2">Cámara</div>
        <div className="flex flex-col items-center gap-3">
          {!isNative && (
            <div className="relative rounded-2xl border overflow-hidden bg-black w-full max-w-[720px]">
              <video ref={vref} autoPlay playsInline muted className="w-full h-[60vh] object-contain" />
              <div className="p-2 flex items-center justify-between">
                <div className="text-xs text-gray-700">{loc ? `${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}` : 'Ubicación no disponible'}</div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={capture} className="px-3 py-1 rounded text-white inline-flex items-center gap-2" style={{ backgroundColor: '#776F74' }}><Camera size={16} /> Capturar</button>
                  <button type="button" onClick={() => setShowGallery((s) => !s)} className="px-3 py-1 rounded border" style={{ borderColor: '#776F74', color: '#776F74' }}>{showGallery ? 'Ocultar capturas' : 'Ver capturas'}</button>
                </div>
              </div>
              <canvas ref={cref} className="hidden" />
              <input ref={fref} type="file" accept="image/*" capture="environment" className="hidden" onChange={onNativeFile} />
            </div>
          )}
          {isNative && (
            <>
              <canvas ref={cref} className="hidden" />
              <input ref={fref} type="file" accept="image/*" capture="environment" className="hidden" onChange={onNativeFile} />
              <button type="button" aria-label="Capturar foto" onClick={() => { try { fref.current && fref.current.click(); } catch {} }} className="fixed bottom-16 right-4 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg z-50" style={{ backgroundColor: '#776F74' }}>
                <Camera size={24} />
              </button>
              <button type="button" onClick={() => setShowGallery((s) => !s)} className="fixed bottom-16 left-4 px-3 py-2 rounded text-white shadow-md z-50" style={{ backgroundColor: '#776F74' }}>{showGallery ? 'Ocultar capturas' : 'Ver capturas'}</button>
            </>
          )}
          {showGallery && (
            <div className="rounded-2xl border p-2 w-full max-w-[720px]">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">Capturas</div>
                <button type="button" onClick={() => setShots([])} className="px-2 py-1 rounded text-white" style={{ backgroundColor: '#776F74' }}>Borrar todas</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {shots.map((u, i) => (
                  <div key={`shot-${i}`} className="border rounded overflow-hidden">
                    <img src={u} alt="shot" className="w-full h-40 object-cover" />
                    <div className="flex items-center justify-between text-xs">
                      <a href={u} download={`captura_${i+1}.jpg`} className="block w-full text-center py-1 bg-gray-100">Descargar</a>
                      <button type="button" onClick={() => deleteShot(i)} className="px-2 py-1">Borrar</button>
                    </div>
                  </div>
                ))}
                {shots.length === 0 && (
                  <div className="text-xs text-gray-600">Sin capturas</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="text-gray-800">
      {!mobileMode && (
        <h2 className="text-2xl font-semibold mb-2">Control Operativo</h2>
      )}
      {showLocation && (
        <p className="text-gray-700">Sede: {title}</p>
      )}
      {!mobileMode && (
        <div className="mt-4 text-sm text-gray-600">
          Seguimiento de operaciones, control de tareas y estados.
        </div>
      )}
  {!mobileMode && (
    <SectionIndexCards
      title="Indice de secciones"
      helperText="Accede a cada frente operativo con una navegacion mas visual y ordenada."
      columnsClassName="xl:grid-cols-2"
      className="mt-4"
      items={[
        {
          key: 'linea_viva',
          title: 'Linea viva',
          description: 'Planificacion, seguimiento e indicadores del proceso',
          icon: MapPin,
          gradient: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)',
          accentGradient: 'linear-gradient(180deg, #3B82F6 0%, #4F46E5 100%)',
          active: coGroup === 'linea_viva',
          expanded: coGroup === 'linea_viva',
          onClick: () => { setCoGroup('linea_viva'); setSection('planificacion'); },
          children: [
            { key: 'lv-planificacion', label: 'Planificacion', icon: Calendar, active: section === 'planificacion', onClick: () => setSection('planificacion') },
            { key: 'lv-seguimiento', label: 'Seguimiento', icon: Search, active: section === 'seguimiento', onClick: () => setSection('seguimiento') },
            { key: 'lv-incidencias', label: 'Incidencias', icon: AlertCircle, active: section === 'incidencias', onClick: () => setSection('incidencias') },
            { key: 'lv-reportes', label: 'Reportes', icon: ClipboardList, active: section === 'reportes', onClick: () => setSection('reportes') },
            { key: 'lv-indicadores', label: 'Indicadores', icon: Download, active: section === 'indicadores', onClick: () => setSection('indicadores') },
          ],
        },
        {
          key: 'proyectos',
          title: 'Proyectos',
          description: 'Control de avance, incidencias y tableros del area',
          icon: Home,
          gradient: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
          accentGradient: 'linear-gradient(180deg, #14B8A6 0%, #0F766E 100%)',
          active: coGroup === 'proyectos',
          expanded: coGroup === 'proyectos',
          onClick: () => { setCoGroup('proyectos'); setSection('planificacion'); },
          children: [
            { key: 'pr-planificacion', label: 'Planificacion', icon: Calendar, active: section === 'planificacion', onClick: () => setSection('planificacion') },
            { key: 'pr-seguimiento', label: 'Seguimiento', icon: Search, active: section === 'seguimiento', onClick: () => setSection('seguimiento') },
            { key: 'pr-incidencias', label: 'Incidencias', icon: AlertCircle, active: section === 'incidencias', onClick: () => setSection('incidencias') },
            { key: 'pr-reportes', label: 'Reportes', icon: ClipboardList, active: section === 'reportes', onClick: () => setSection('reportes') },
            { key: 'pr-indicadores', label: 'Indicadores', icon: Download, active: section === 'indicadores', onClick: () => setSection('indicadores') },
          ],
        },
        {
          key: 'gruas',
          title: 'Gruas',
          description: 'Operacion, seguimiento e incidencias de maniobras',
          icon: RefreshCcw,
          gradient: 'linear-gradient(135deg, #EA580C 0%, #F59E0B 100%)',
          accentGradient: 'linear-gradient(180deg, #F97316 0%, #EA580C 100%)',
          active: coGroup === 'gruas',
          expanded: coGroup === 'gruas',
          onClick: () => { setCoGroup('gruas'); setSection('planificacion'); },
          children: [
            { key: 'gr-planificacion', label: 'Planificacion', icon: Calendar, active: section === 'planificacion', onClick: () => setSection('planificacion') },
            { key: 'gr-seguimiento', label: 'Seguimiento', icon: Search, active: section === 'seguimiento', onClick: () => setSection('seguimiento') },
            { key: 'gr-incidencias', label: 'Incidencias', icon: AlertCircle, active: section === 'incidencias', onClick: () => setSection('incidencias') },
            { key: 'gr-reportes', label: 'Reportes', icon: ClipboardList, active: section === 'reportes', onClick: () => setSection('reportes') },
            { key: 'gr-indicadores', label: 'Indicadores', icon: Download, active: section === 'indicadores', onClick: () => setSection('indicadores') },
          ],
        },
        {
          key: 'alumbrado_publico',
          title: 'Alumbrado publico',
          description: 'Reportes, validacion y exportes de ejecucion',
          icon: Lightbulb,
          gradient: 'linear-gradient(135deg, #16A34A 0%, #84CC16 100%)',
          accentGradient: 'linear-gradient(180deg, #84CC16 0%, #16A34A 100%)',
          active: coGroup === 'alumbrado_publico',
          expanded: coGroup === 'alumbrado_publico',
          onClick: () => { setCoGroup('alumbrado_publico'); setApTab('reportes'); },
          children: [
            { key: 'ap-reportes', label: 'Reportes', icon: ClipboardList, active: apTab === 'reportes', onClick: () => setApTab('reportes') },
            { key: 'ap-validacion', label: 'Validacion de reportes', icon: CheckCircle, active: apTab === 'validacion_reportes', onClick: () => setApTab('validacion_reportes') },
            { key: 'ap-exporte', label: 'Exporte de ejecucion', icon: Download, active: apTab === 'exporte_ejecucion', onClick: () => setApTab('exporte_ejecucion') },
          ],
        },
        {
          key: 'administracion',
          title: 'Administracion',
          description: 'Configuracion general y usuarios de brigada',
          icon: User,
          gradient: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)',
          accentGradient: 'linear-gradient(180deg, #A855F7 0%, #7C3AED 100%)',
          active: coGroup === 'administracion',
          expanded: coGroup === 'administracion',
          onClick: () => { setCoGroup('administracion'); setSection('configuracion'); },
          children: [
            { key: 'ad-configuracion', label: 'Configuracion', icon: Key, active: section === 'configuracion', onClick: () => setSection('configuracion') },
            { key: 'ad-brigada', label: 'Usuarios de brigada', icon: User, active: section === 'brigada', onClick: () => setSection('brigada') },
          ],
        },
      ]}
    />
  )}

  {mobileMode && (
    <div className="w-full px-4 py-2" style={{ backgroundColor: isOnline ? '#EAFBF2' : '#FEF3C7', border: '1px solid #E5E7EB', borderRadius: '0.75rem', marginTop: '0.75rem' }}>
      <div className="flex items-center justify-between text-sm">
        <span className={isOnline ? 'text-green-700' : 'text-yellow-800'}>{isOnline ? 'Conectado' : 'Sin conexión'}</span>
        <div className="flex items-center gap-2">
          {lastSyncAt && (<span className="text-gray-600">Último sync: {new Date(lastSyncAt).toLocaleString()}</span>)}
          <button type="button" className="px-2 py-1 rounded border text-xs" disabled={syncing || !isOnline || !pendingSync.length} onClick={processSyncQueue}>{syncing ? 'Sincronizando...' : 'Sincronizar ahora'}</button>
        </div>
      </div>
    </div>
  )}

      

      {(!mobileMode && coGroup === 'alumbrado_publico' && apTab === 'reportes') && (
        <section id="alumbrado-reportes" className="bg-white rounded-xl shadow border border-gray-200 p-5 mt-4">
          <h4 className="text-base font-semibold mb-2">Alumbrado publico - Reportes</h4>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="text-sm text-gray-600">
              Crea un reporte completando la informacion solicitada y quedara guardado en el historial.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => { setApReporteSaveError(''); setIsApReporteOpen(true); }}
                className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold shadow-sm hover:shadow hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PlusCircle size={18} />
                <span className="flex flex-col leading-tight text-left">
                  <span>Crear reportes</span>
                  <span className="text-xs font-normal text-white/90">Con esta informacion</span>
                </span>
              </button>
              <button
                type="button"
                onClick={exportApReportesTable}
                disabled={!apReportesTableRows.length}
                className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 font-semibold shadow-sm hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Download size={18} />
                <span>Exportar</span>
              </button>
            </div>
          </div>

          <Modal isOpen={isApReporteOpen} onClose={() => setIsApReporteOpen(false)} title="Crear Reporte" size="max-w-4xl">
            <form onSubmit={saveApReporte} className="space-y-5">
              <div className="text-sm text-gray-600">Registro de incidencia en campo</div>
              {apReporteSaveError ? (
                <div className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
                  {apReporteSaveError}
                </div>
              ) : null}

              <div className="space-y-3">
                <div className="text-[11px] font-semibold tracking-widest text-gray-400">IDENTIFICACIÓN</div>
                <div className="h-px bg-gray-100" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">BRIGADA</label>
                    <select
                      className="w-full border rounded-lg px-3 py-2 bg-white"
                      value={apReporteForm.grupo_ejecutor || ''}
                      onChange={(e) => setApReporteForm((f) => ({ ...f, grupo_ejecutor: e.target.value }))}
                    >
                      <option value="">Seleccionar...</option>
                      {Array.from(
                        new Set((brigUsers || [])
                          .filter((u) => String(u?.tipo_proceso || '').trim() === 'alumbrado_publico')
                          .map((u) => String(u?.brigada || '').trim())
                          .filter(Boolean))
                      )
                        .sort((a, b) => a.localeCompare(b))
                        .map((b) => (
                          <option key={`brg-${b}`} value={b}>{b}</option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">CÓDIGO <span className="text-red-600">*</span></label>
                    <input
                      type="text"
                      placeholder="Ej. REP-0024"
                      className="w-full border rounded-lg px-3 py-2"
                      value={apReporteForm.codigo}
                      onChange={(e) => setApReporteForm((f) => ({ ...f, codigo: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">ESTADO REPORTE</label>
                    <select
                      className="w-full border rounded-lg px-3 py-2 bg-white"
                      value={apReporteForm.estado_reporte || ''}
                      onChange={(e) => setApReporteForm((f) => ({ ...f, estado_reporte: e.target.value }))}
                    >
                      <option value="">Seleccionar...</option>
                      <option value="Pendiente">Pendiente</option>
                      <option value="Programado">Programado</option>
                      <option value="Finalizado">Finalizado</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-[11px] font-semibold tracking-widest text-gray-400">UBICACIÓN</div>
                <div className="h-px bg-gray-100" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">COMUNA</label>
                    <select
                      className="w-full border rounded-lg px-3 py-2 bg-white"
                      value={apReporteForm.comuna || ''}
                      onChange={(e) => setApReporteForm((f) => ({ ...f, comuna: e.target.value }))}
                    >
                      <option value="">Seleccionar...</option>
                      {Array.from({ length: 7 }).map((_, i) => {
                        const v = `COMUNA ${i + 1}`;
                        return <option key={`comuna-${i + 1}`} value={v}>{v}</option>;
                      })}
                      <option value="ZONA RURAL">ZONA RURAL</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">BARRIO</label>
                    <input type="text" placeholder="Nombre del barrio" className="w-full border rounded-lg px-3 py-2" value={apReporteForm.barrio} onChange={(e) => setApReporteForm((f) => ({ ...f, barrio: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">LATITUD</label>
                    <input type="text" placeholder="-33.4500" className="w-full border rounded-lg px-3 py-2" value={apReporteForm.latitud} onChange={(e) => setApReporteForm((f) => ({ ...f, latitud: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">LONGITUD</label>
                    <input type="text" placeholder="-70.6670" className="w-full border rounded-lg px-3 py-2" value={apReporteForm.longitud} onChange={(e) => setApReporteForm((f) => ({ ...f, longitud: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">TIPO DE REPLANTEO</label>
                    <input type="text" placeholder="Tipo de replanteo" className="w-full border rounded-lg px-3 py-2" value={apReporteForm.tipo_replanteo || ''} onChange={(e) => setApReporteForm((f) => ({ ...f, tipo_replanteo: e.target.value }))} />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-[11px] font-semibold tracking-widest text-gray-400">DETALLE DE INCIDENCIA</div>
                <div className="h-px bg-gray-100" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">TIPO DE DAÑO</label>
                    <select
                      className="w-full border rounded-lg px-3 py-2 bg-white"
                      value={apReporteForm.tipo_dano || ''}
                      onChange={(e) => setApReporteForm((f) => ({ ...f, tipo_dano: e.target.value }))}
                    >
                      <option value="">Seleccionar...</option>
                      <option value="Luminaria apagada">Luminaria apagada</option>
                      <option value="Luminaria intermitente">Luminaria intermitente</option>
                      <option value="Poste caído">Poste caído</option>
                      <option value="Cableado">Cableado</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">NODO REPORTADO</label>
                    <input type="text" placeholder="ID del nodo" className="w-full border rounded-lg px-3 py-2" value={apReporteForm.nodo_reportado} onChange={(e) => setApReporteForm((f) => ({ ...f, nodo_reportado: e.target.value }))} />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-[11px] font-semibold tracking-widest text-gray-400">TEMPORALIDAD</div>
                <div className="h-px bg-gray-100" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">FECHA DEL REPORTE <span className="text-red-600">*</span></label>
                    <input type="date" className="w-full border rounded-lg px-3 py-2" value={apReporteForm.fecha_limite} onChange={(e) => setApReporteForm((f) => ({ ...f, fecha_limite: e.target.value }))} required />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 pt-2">
                <div className="text-xs text-red-600">* Campos obligatorios</div>
                <div className="flex gap-2">
                  <button type="button" className="px-5 py-2 rounded-lg border hover:bg-gray-50" onClick={() => setIsApReporteOpen(false)}>Cancelar</button>
                  <button type="submit" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                    <Save size={18} />
                    Guardar reporte
                  </button>
                </div>
              </div>
            </form>
          </Modal>
          {apReportesLoading ? (
            <p className="text-sm text-gray-600">Cargando...</p>
          ) : apReportes.length === 0 ? (
            <p className="text-sm text-gray-600">Sin registros.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left">CREADO</th>
                    <th className="px-2 py-2 text-left">BRIGADA</th>
                    <th className="px-2 py-2 text-left">CODIGO</th>
                    <th className="px-2 py-2 text-left">ESTADO REPORTE</th>
                    <th className="px-2 py-2 text-left">OBSERVACIONES</th>
                    <th className="px-2 py-2 text-left">COMUNA</th>
                    <th className="px-2 py-2 text-left">BARRIO</th>
                    <th className="px-2 py-2 text-left">TIPO DAÑO</th>
                    <th className="px-2 py-2 text-left">NODO REPORTADO</th>
                    <th className="px-2 py-2 text-left">LATITUD</th>
                    <th className="px-2 py-2 text-left">LONGITUD</th>
                    <th className="px-2 py-2 text-left">TIPO REPLANTEO</th>
                    <th className="px-2 py-2 text-left">FECHA LIMITE</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {apReportesTableRows.map((r) => (
                    <tr key={`apr-${r.id}`} className="border-t">
                      <td className="px-2 py-2 whitespace-nowrap">{r.creado}</td>
                      <td className="px-2 py-2">{r.brigada}</td>
                      <td className="px-2 py-2">{r.codigo}</td>
                      <td className="px-2 py-2">{r.estadoReporte}</td>
                      <td className="px-2 py-2 min-w-[220px]">{r.observaciones}</td>
                      <td className="px-2 py-2">{r.comuna}</td>
                      <td className="px-2 py-2">{r.barrio}</td>
                      <td className="px-2 py-2">{r.tipoDano}</td>
                      <td className="px-2 py-2">{r.nodoReportado}</td>
                      <td className="px-2 py-2">{r.latitud}</td>
                      <td className="px-2 py-2">{r.longitud}</td>
                      <td className="px-2 py-2">{r.tipoReplanteo}</td>
                      <td className="px-2 py-2">{r.fechaLimite}</td>
                      <td className="px-2 py-2 text-right">
                        <button type="button" className="text-red-600" onClick={() => deleteApReporte(r.id)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {(!mobileMode && coGroup === 'alumbrado_publico' && apTab === 'validacion_reportes') && (
        <section id="alumbrado-mat" className="bg-white rounded-xl shadow border border-gray-200 p-5 mt-4">
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F59E0B' }}>
                <span className="text-white text-lg leading-none">💡</span>
              </div>
              <div>
                <div className="text-base font-semibold">Alumbrado Público</div>
                <div className="text-[11px] tracking-widest text-gray-500">MÓDULO / VALIDACIÓN DE REPORTES</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Consultar por CÓDIGO..."
                className="w-full border rounded-xl pl-9 pr-3 py-2 bg-white text-sm"
                value={apValidCodigo}
                onChange={(e) => setApValidCodigo(e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="px-5 py-2 rounded-xl text-white text-sm font-semibold"
                style={{ backgroundColor: '#D97706' }}
                onClick={() => { setApValidReporteEditing(false); setApValidReporteDraft(null); fetchApValidacion(apValidCodigo); setApMatEditId(''); setApMatForm((f) => ({ ...f, id: undefined, codigo: String(apValidCodigo || '').trim() })); }}
              >
                Consultar
              </button>
              <button
                type="button"
                className="px-5 py-2 rounded-xl border bg-white hover:bg-gray-50 text-sm font-medium"
                onClick={() => { setApValidReporte(null); setApValidMateriales([]); setApValidReporteDraft(null); setApValidReporteEditing(false); setApMatEditId(''); setApMatForm((f) => ({ ...f, id: undefined })); }}
              >
                Limpiar
              </button>
            </div>
          </div>

          {apValidLoading ? (
            <p className="text-sm text-gray-600">Consultando...</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                <div className="bg-white border border-gray-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="text-[11px] font-semibold tracking-widest text-yellow-600">• REPORTE</div>
                    <div className="flex items-center gap-2">
                      {apValidReporteEditing ? (
                        <>
                          <button
                            type="button"
                            className="px-4 py-2 rounded-xl text-white text-sm font-semibold"
                            style={{ backgroundColor: '#D97706' }}
                            onClick={saveApValidReporte}
                          >
                            Guardar
                          </button>
                          <button
                            type="button"
                            className="px-4 py-2 rounded-xl border bg-white hover:bg-gray-50 text-sm font-medium"
                            onClick={() => { setApValidReporteDraft(apValidReporte || null); setApValidReporteEditing(false); }}
                          >
                            Cancelar
                          </button>
                        </>
                      ) : apValidReporte ? (
                        <button
                          type="button"
                          className="px-4 py-2 rounded-xl border bg-white hover:bg-gray-50 text-sm font-medium"
                          onClick={() => { setApValidReporteDraft(apValidReporte); setApValidReporteEditing(true); }}
                        >
                          Editar
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {!apValidReporte && !apValidReporteEditing ? (
                    <div className="text-sm text-gray-500 italic mb-3">Sin reporte para este código.</div>
                  ) : null}

                  <div className="space-y-4 text-sm">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">CÓDIGO</label>
                          <input
                            type="text"
                            className={`w-full border rounded-xl px-3 py-2 ${apValidReporte ? 'bg-gray-50' : 'bg-white'}`}
                            value={apValidReporteDraft?.codigo || ''}
                            disabled={!!apValidReporte}
                            onChange={(e) => {
                              if (apValidReporte) return;
                              const v = e.target.value;
                              setApValidCodigo(v);
                              setApValidReporteDraft((f) => ({ ...(f || {}), codigo: v }));
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">ESTADO REPORTE</label>
                          <select
                            className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`}
                            value={apValidReporteDraft?.estado_reporte || ''}
                            onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), estado_reporte: e.target.value }))}
                            disabled={!apValidReporteEditing && !!apValidReporte}
                          >
                            <option value="">Seleccionar...</option>
                            <option value="Pendiente">Pendiente</option>
                            <option value="Programado">Programado</option>
                            <option value="Finalizado">Finalizado</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">COMUNA</label>
                          <select
                            className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`}
                            value={apValidReporteDraft?.comuna || ''}
                            onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), comuna: e.target.value }))}
                            disabled={!apValidReporteEditing && !!apValidReporte}
                          >
                            <option value="">Seleccionar...</option>
                            {Array.from({ length: 7 }).map((_, i) => {
                              const v = `COMUNA ${i + 1}`;
                              return <option key={`val-comuna-${i + 1}`} value={v}>{v}</option>;
                            })}
                            <option value="ZONA RURAL">ZONA RURAL</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">BARRIO</label>
                          <input type="text" className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`} value={apValidReporteDraft?.barrio || ''} onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), barrio: e.target.value }))} disabled={!apValidReporteEditing && !!apValidReporte} />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">TIPO DE DAÑO</label>
                          <input type="text" className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`} value={apValidReporteDraft?.tipo_dano || ''} onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), tipo_dano: e.target.value }))} disabled={!apValidReporteEditing && !!apValidReporte} />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">NODO REPORTADO</label>
                          <input type="text" className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`} value={apValidReporteDraft?.nodo_reportado || ''} onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), nodo_reportado: e.target.value }))} disabled={!apValidReporteEditing && !!apValidReporte} />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">LATITUD</label>
                          <input type="text" className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`} value={apValidReporteDraft?.latitud || ''} onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), latitud: e.target.value }))} disabled={!apValidReporteEditing && !!apValidReporte} />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">LONGITUD</label>
                          <input type="text" className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`} value={apValidReporteDraft?.longitud || ''} onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), longitud: e.target.value }))} disabled={!apValidReporteEditing && !!apValidReporte} />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">FECHA LÍMITE</label>
                          <input type="date" className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`} value={apValidReporteDraft?.fecha_limite || ''} onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), fecha_limite: e.target.value }))} disabled={!apValidReporteEditing && !!apValidReporte} />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">TIPO DE REPLANTEO</label>
                          <input type="text" className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`} value={apValidReporteDraft?.tipo_replanteo || ''} onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), tipo_replanteo: e.target.value }))} disabled={!apValidReporteEditing && !!apValidReporte} />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">NODO REAL</label>
                          <input type="text" className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`} value={apValidReporteDraft?.nodo_real || ''} onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), nodo_real: e.target.value }))} disabled={!apValidReporteEditing && !!apValidReporte} />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">COORDENADA REAL</label>
                          <input type="text" className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`} value={apValidReporteDraft?.coordenada_real || ''} onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), coordenada_real: e.target.value }))} disabled={!apValidReporteEditing && !!apValidReporte} />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">FOTO REFERENCIA (URL)</label>
                          <input type="text" className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`} value={apValidReporteDraft?.foto_referencia || ''} onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), foto_referencia: e.target.value }))} disabled={!apValidReporteEditing && !!apValidReporte} />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">BRIGADA</label>
                          <select
                            className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`}
                            value={apValidReporteDraft?.grupo_ejecutor || ''}
                            onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), grupo_ejecutor: e.target.value }))}
                            disabled={!apValidReporteEditing && !!apValidReporte}
                          >
                            <option value="">Seleccionar...</option>
                            {Array.from(
                              new Set((brigUsers || [])
                                .filter((u) => String(u?.tipo_proceso || '').trim() === 'alumbrado_publico')
                                .map((u) => String(u?.brigada || '').trim())
                                .filter(Boolean))
                            )
                              .sort((a, b) => a.localeCompare(b))
                              .map((b) => (
                                <option key={`val-brg-${b}`} value={b}>{b}</option>
                              ))}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">ESTADO</label>
                          <input type="text" className={`w-full border rounded-xl px-3 py-2 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`} value={apValidReporteDraft?.estado || ''} onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), estado: e.target.value }))} disabled={!apValidReporteEditing && !!apValidReporte} />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">OBSERVACIONES</label>
                        <textarea className={`w-full border rounded-xl px-3 py-2 min-h-24 ${(!apValidReporteEditing && !!apValidReporte) ? 'bg-gray-50' : 'bg-white'}`} value={apValidReporteDraft?.observaciones || ''} onChange={(e) => setApValidReporteDraft((f) => ({ ...(f || {}), observaciones: e.target.value }))} disabled={!apValidReporteEditing && !!apValidReporte} />
                      </div>
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="text-[11px] font-semibold tracking-widest text-yellow-600">• MATERIAL INSTALADO</div>
                    <button
                      type="button"
                      className="px-4 py-2 rounded-xl border bg-white hover:bg-gray-50 text-sm font-medium"
                      onClick={() => {
                        const codigo = String(apValidCodigo || apMatForm.codigo || '').trim();
                        setApMatEditId('');
                        setApMatForm({
                          codigo,
                          instala_material: 'Si',
                          material_item: '',
                          cantidad: '',
                          unidad: '',
                          aplica_serial: 'No',
                          numero_serial: '',
                          observaciones: '',
                          nodo_instalacion: '',
                          hora_inicio: '',
                          hora_fin: '',
                          foto_instalado: '',
                          coordenada: ''
                        });
                      }}
                    >
                      Nuevo
                    </button>
                  </div>

                  {apValidMateriales.length === 0 ? (
                    <div className="text-sm text-gray-500 italic mb-3">Sin material instalado para este código.</div>
                  ) : (
                    <div className="overflow-x-auto mb-4">
                      <table className="min-w-full text-xs border">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-2 py-2 text-left">ITEM</th>
                            <th className="px-2 py-2 text-left">CANTIDAD</th>
                            <th className="px-2 py-2 text-left">UNIDAD</th>
                            <th className="px-2 py-2 text-left">SERIAL</th>
                            <th className="px-2 py-2 text-left">NODO</th>
                            <th className="px-2 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {apValidMateriales.map((m) => (
                            <tr key={`valm-${m.id}`} className="border-t">
                              <td className="px-2 py-2">{m.material_item || '-'}</td>
                              <td className="px-2 py-2">{m.cantidad ?? '-'}</td>
                              <td className="px-2 py-2">{m.unidad || '-'}</td>
                              <td className="px-2 py-2">{m.numero_serial || '-'}</td>
                              <td className="px-2 py-2">{m.nodo_instalacion || '-'}</td>
                              <td className="px-2 py-2 text-right whitespace-nowrap">
                                <button
                                  type="button"
                                  className="text-blue-600 mr-3"
                                  onClick={() => {
                                    setApMatEditId(String(m.id));
                                    setApMatForm({
                                      id: m.id,
                                      codigo: String(m.codigo || apValidCodigo || '').trim(),
                                      instala_material: m.instala_material || 'Si',
                                      material_item: m.material_item || '',
                                      cantidad: m.cantidad ?? '',
                                      unidad: m.unidad || '',
                                      aplica_serial: m.aplica_serial || 'No',
                                      numero_serial: m.numero_serial || '',
                                      observaciones: m.observaciones || '',
                                      nodo_instalacion: m.nodo_instalacion || '',
                                      hora_inicio: m.hora_inicio || '',
                                      hora_fin: m.hora_fin || '',
                                      foto_instalado: m.foto_instalado || '',
                                      coordenada: m.coordenada || ''
                                    });
                                  }}
                                >
                                  Editar
                                </button>
                                <button type="button" className="text-red-600" onClick={() => deleteApMat(m.id)}>Eliminar</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="text-[11px] font-semibold tracking-widest text-gray-500 mb-3">REGISTRO DE INSTALACIÓN</div>
                  <form
                    onSubmit={(e) => {
                      if (apMatEditId) {
                        e?.preventDefault?.();
                        saveApMat(e);
                        return;
                      }
                      saveApMat(e);
                    }}
                    className="space-y-4 text-sm"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">CÓDIGO</label>
                        <input type="text" placeholder="Ej. ALB-00123" className="w-full border rounded-xl px-3 py-2" value={apMatForm.codigo} onChange={(e) => setApMatForm((f) => ({ ...f, codigo: e.target.value }))} required />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">¿INSTALA MATERIAL?</label>
                        <select className="w-full border rounded-xl px-3 py-2 bg-white" value={apMatForm.instala_material} onChange={(e) => setApMatForm((f) => ({ ...f, instala_material: e.target.value }))}>
                          <option value="Si">Si</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">TIPO DE MATERIAL / ITEM</label>
                        <input type="text" placeholder="Ej. Luminaria LED" className="w-full border rounded-xl px-3 py-2" value={apMatForm.material_item} onChange={(e) => setApMatForm((f) => ({ ...f, material_item: e.target.value }))} />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">CANTIDAD</label>
                        <input type="number" placeholder="0" className="w-full border rounded-xl px-3 py-2" value={apMatForm.cantidad} onChange={(e) => setApMatForm((f) => ({ ...f, cantidad: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">UNIDAD</label>
                        <input type="text" placeholder="Ej. Und, m, kg" className="w-full border rounded-xl px-3 py-2" value={apMatForm.unidad} onChange={(e) => setApMatForm((f) => ({ ...f, unidad: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">¿APLICA SERIAL?</label>
                        <select className="w-full border rounded-xl px-3 py-2 bg-white" value={apMatForm.aplica_serial} onChange={(e) => setApMatForm((f) => ({ ...f, aplica_serial: e.target.value }))}>
                          <option value="No">No</option>
                          <option value="Si">Si</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">NÚMERO DE SERIAL</label>
                        <input type="text" placeholder="—" className="w-full border rounded-xl px-3 py-2" value={apMatForm.numero_serial} onChange={(e) => setApMatForm((f) => ({ ...f, numero_serial: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">NODO DE INSTALACIÓN</label>
                        <input type="text" placeholder="Ej. NODO-045" className="w-full border rounded-xl px-3 py-2" value={apMatForm.nodo_instalacion} onChange={(e) => setApMatForm((f) => ({ ...f, nodo_instalacion: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">COORDENADA</label>
                        <input type="text" placeholder="Lat, Lng" className="w-full border rounded-xl px-3 py-2" value={apMatForm.coordenada} onChange={(e) => setApMatForm((f) => ({ ...f, coordenada: e.target.value }))} />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">FECHA / HORA INICIO</label>
                        <input type="datetime-local" className="w-full border rounded-xl px-3 py-2" value={apMatForm.hora_inicio} onChange={(e) => setApMatForm((f) => ({ ...f, hora_inicio: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">FECHA / HORA FIN</label>
                        <input type="datetime-local" className="w-full border rounded-xl px-3 py-2" value={apMatForm.hora_fin} onChange={(e) => setApMatForm((f) => ({ ...f, hora_fin: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">FOTO INSTALADO (URL)</label>
                        <input type="text" placeholder="https://" className="w-full border rounded-xl px-3 py-2" value={apMatForm.foto_instalado} onChange={(e) => setApMatForm((f) => ({ ...f, foto_instalado: e.target.value }))} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold tracking-widest text-gray-500 mb-1">OBSERVACIONES</label>
                      <textarea className="w-full border rounded-xl px-3 py-2 min-h-24" placeholder="Ingrese observaciones adicionales..." value={apMatForm.observaciones} onChange={(e) => setApMatForm((f) => ({ ...f, observaciones: e.target.value }))} />
                    </div>

                    <div className="h-px bg-gray-100" />

                    <div className="flex items-center justify-between gap-3">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs border bg-gray-50 text-gray-600">
                        {apValidMateriales.length === 0 ? 'Sin registros' : `${apValidMateriales.length} registros`}
                      </div>
                      <button type="submit" className="inline-flex items-center gap-2 px-6 py-2 rounded-xl text-white text-sm font-semibold" style={{ backgroundColor: '#D97706' }}>
                        <Save size={16} />
                        {apMatEditId ? 'Actualizar' : 'Guardar'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {(!mobileMode && coGroup === 'alumbrado_publico' && apTab === 'exporte_ejecucion') && (
        <section id="alumbrado-exporte" className="bg-white rounded-xl shadow border border-gray-200 p-5 mt-4">
          <h4 className="text-base font-semibold mb-2">Alumbrado público - Exporte de ejecución</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              type="date"
              className="border rounded px-3 py-2"
              value={apExportFrom}
              onChange={(e) => setApExportFrom(e.target.value)}
            />
            <input
              type="date"
              className="border rounded px-3 py-2"
              value={apExportTo}
              onChange={(e) => setApExportTo(e.target.value)}
            />
            <div className="md:col-span-2 flex gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded bg-blue-600 text-white"
                disabled={apExportLoading || !apExportFrom || !apExportTo}
                onClick={async () => {
                  const from = String(apExportFrom || '').trim();
                  const to = String(apExportTo || '').trim();
                  if (!from || !to) return;
                  setApExportLoading(true);
                  try {
                    const res = await api.alumbradoExporte.get({ from, to });
                    if (!res?.ok) return;
                    const reportes = Array.isArray(res.reportes) ? res.reportes : [];
                    const materiales = Array.isArray(res.materiales) ? res.materiales : [];
                    const rowsReportes = reportes.map((r) => ({
                      CREADO: r?.created_at ? String(r.created_at).slice(0, 19).replace('T', ' ') : '-',
                      BRIGADA: r?.grupo_ejecutor || '-',
                      CODIGO: r?.codigo || '-',
                      ESTADO_REPORTE: r?.estado_reporte || '-',
                      OBSERVACIONES: r?.observaciones || '-',
                      COMUNA: r?.comuna || '-',
                      BARRIO: r?.barrio || '-',
                      TIPO_DANO: r?.tipo_dano || '-',
                      NODO_REPORTADO: r?.nodo_reportado || '-',
                      LATITUD: r?.latitud || '-',
                      LONGITUD: r?.longitud || '-',
                      TIPO_REPLANTEO: r?.tipo_replanteo || '-',
                      FECHA_LIMITE: r?.fecha_limite || '-',
                    }));
                    const headersMat = [
                      'id',
                      'codigo',
                      'instala_material',
                      'material_item',
                      'cantidad',
                      'unidad',
                      'aplica_serial',
                      'numero_serial',
                      'observaciones',
                      'nodo_instalacion',
                      'hora_inicio',
                      'hora_fin',
                      'foto_instalado',
                      'coordenada',
                      'created_at',
                      'updated_at',
                      'deleted_at'
                    ];
                    const wb = XLSX.utils.book_new();
                    const ws1 = XLSX.utils.json_to_sheet(rowsReportes);
                    const ws2 = XLSX.utils.json_to_sheet(materiales, { header: headersMat });
                    XLSX.utils.book_append_sheet(wb, ws1, 'Reportes');
                    XLSX.utils.book_append_sheet(wb, ws2, 'Mat_instalado');
                    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'control-operativo', reportName: 'Alumbrado público (reportes y material)' });
                    XLSX.writeFile(wb, `alumbrado_${from}_a_${to}.xlsx`);
                  } finally {
                    setApExportLoading(false);
                  }
                }}
              >
                {apExportLoading ? 'Exportando...' : 'Exportar Excel'}
              </button>
            </div>
          </div>
          <div className="text-sm text-gray-600 mt-3">
            Exporta un archivo Excel por rango de fechas (FECHA LIMITE), con dos hojas: Reportes y Mat_instalado.
          </div>
        </section>
      )}

      {(!mobileMode && ['linea_viva', 'proyectos', 'gruas'].includes(coGroup) && section === 'planificacion') && (
        <section id="planificacion" className="bg-white rounded-xl shadow border border-gray-200 p-5 mt-4">
          <h4 className="text-base font-semibold mb-2">Planificación</h4>
          <div className="mt-2">
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <h5 className="text-sm font-semibold">Gestión de ordenes</h5>
                <div className="flex gap-2">
                  {(coGroup !== 'proyectos' || canEjecutarAsignacionAlmacen) && (
                    <button type="button" onClick={() => setIsOrderOpen(true)} className="px-3 py-1 text-sm rounded-md bg-blue-600 text-white">Nueva Orden</button>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">Organización y planificación de órdenes.</p>
              {coGroup === 'proyectos' && canDocumentarProyecto && !canEjecutarAsignacionAlmacen && (
                <p className="text-xs text-teal-800 mt-2 bg-teal-50 border border-teal-100 rounded px-2 py-1">
                  Perfil de documentación: registra el proyecto abajo. La entrega de material del coordinador se hace en Almacén → Asignación (permiso distinto).
                </p>
              )}
              {statusMsg && <p className="text-xs text-green-700 mt-2">{statusMsg}</p>}
            </div>
          </div>
          {coGroup === 'proyectos' && canDocumentarProyecto && (
            <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-white">
              <h5 className="text-sm font-semibold text-gray-900">Documentación de proyecto</h5>
              <p className="text-xs text-gray-500 mt-1">Avance de obra en Control Operativo (grupo Proyectos). No incluye vehículo ni formulario de asignación de almacén.</p>
              <form className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" onSubmit={saveGroupRecord}>
                {sessionDocumentedPerson?.person_id ? (
                  <div className="md:col-span-2 rounded border border-teal-100 bg-teal-50 px-3 py-2 text-sm text-teal-900">
                    Documenta como: <strong>{sessionDocumentedPerson.name || 'Brigadista'}</strong>
                    {currentUser?.responsibleCedula || currentUser?.responsible_cedula ? (
                      <span className="text-xs text-teal-700 block">Sesión ligada a su ficha RRHH (sin paso por asignación de almacén).</span>
                    ) : null}
                  </div>
                ) : (
                  <div className="md:col-span-2">
                    <label className="text-xs text-gray-600">Brigadista que documenta</label>
                    <select
                      className="w-full border rounded px-3 py-2 text-sm bg-white"
                      value={groupForm.documentedPersonId}
                      onChange={(e) => setGroupForm((f) => ({ ...f, documentedPersonId: e.target.value }))}
                      required
                      disabled={docPeopleLoading}
                    >
                      <option value="">{docPeopleLoading ? 'Cargando colaboradores…' : 'Seleccione brigadista…'}</option>
                      {docPeople.map((p) => {
                        const pid = p._dbId ?? p.id;
                        const label = `${p.name || 'Sin nombre'} — ${p.dni || p.id || pid}`;
                        return (
                          <option key={String(pid)} value={String(pid)}>{label}</option>
                        );
                      })}
                    </select>
                    {!docPeopleLoading && docPeople.length === 0 && (
                      <p className="text-xs text-amber-700 mt-1">No hay colaboradores activos con rol operativo de brigadista en RRHH.</p>
                    )}
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-600">Título / código de obra</label>
                  <input type="text" className="w-full border rounded px-3 py-2 text-sm" value={groupForm.title} onChange={(e) => setGroupForm((f) => ({ ...f, title: e.target.value }))} required />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-600">Descripción / actividades</label>
                  <textarea className="w-full border rounded px-3 py-2 text-sm" rows={3} value={groupForm.description} onChange={(e) => setGroupForm((f) => ({ ...f, description: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Estado</label>
                  <select className="w-full border rounded px-3 py-2 text-sm" value={groupForm.status} onChange={(e) => setGroupForm((f) => ({ ...f, status: e.target.value }))}>
                    <option value="Pendiente">Pendiente</option>
                    <option value="En proceso">En proceso</option>
                    <option value="Completado">Completado</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600">Fecha</label>
                  <input type="date" className="w-full border rounded px-3 py-2 text-sm" value={groupForm.date} onChange={(e) => setGroupForm((f) => ({ ...f, date: e.target.value }))} />
                </div>
                <div className="md:col-span-2 flex justify-end">
                  <button type="submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-teal-600 text-white text-sm">
                    <Save size={16} /> Guardar documentación
                  </button>
                </div>
              </form>
              {groupRecords.length > 0 && (
                <ul className="mt-4 divide-y border rounded-lg">
                  {groupRecords.map((r) => (
                    <li key={r.id} className="px-3 py-2 flex items-start justify-between gap-2 text-sm">
                      <div>
                        <div className="font-medium">{r.title}</div>
                        <div className="text-gray-600 text-xs">{r.status}{r.date ? ` · ${r.date}` : ''}</div>
                        {r.description && <div className="text-gray-700 text-xs mt-1">{String(r.description).slice(0, 200)}</div>}
                      </div>
                      <button type="button" className="text-red-600 text-xs shrink-0" onClick={() => deleteGroupRecord(r.id)}>Eliminar</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}
      {(!mobileMode && ['linea_viva', 'proyectos', 'gruas'].includes(coGroup) && section === 'seguimiento') && (
        <section id="seguimiento" className="bg-white rounded-xl shadow border border-gray-200 p-5 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-base font-semibold">Vista de Calendario</h4>
            <div className="flex items-center gap-2">
              <div className="relative">
                <input type="text" placeholder="Buscar..." className="border rounded px-3 py-1 pl-8" value={segQuery} onChange={(e) => setSegQuery(e.target.value)} />
                <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
              </div>
              <select className="border rounded px-3 py-1" value={segBrigId} onChange={(e) => setSegBrigId(e.target.value)}>
                <option value="">Todos los usuarios</option>
                {brigUsersForProcess.map((u) => (
                  <option key={`segbrig-${u.id}`} value={u.id}>
                    {u.brigada || u.nombre}
                  </option>
                ))}
              </select>
              <select className="border rounded px-3 py-1" value={segStatus} onChange={(e) => setSegStatus(e.target.value)}>
                <option value="Todos">Todos</option>
                <option value="Pendiente">Pendiente</option>
                <option value="En progreso">En proceso</option>
                <option value="Completado">Completado</option>
                <option value="Cancelado">Cancelado</option>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <button type="button" onClick={gotoPrevMonth} className="px-2 py-1 rounded border border-gray-300 bg-white"><ChevronLeft size={16} /></button>
              <div className="text-sm text-gray-700">{segMonthLabel}</div>
              <button type="button" onClick={gotoNextMonth} className="px-2 py-1 rounded border border-gray-300 bg-white"><ChevronRight size={16} /></button>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2 py-1 rounded bg-gray-100 border">Pendiente: {segSummary['pendiente']}</span>
              <span className="px-2 py-1 rounded bg-gray-100 border">En proceso: {segSummary['en progreso']}</span>
              <span className="px-2 py-1 rounded bg-gray-100 border">Completado: {segSummary['completado']}</span>
              <span className="px-2 py-1 rounded bg-gray-100 border">Cancelado: {segSummary['cancelado']}</span>
            </div>
          </div>
          <div className="grid grid-cols-7 text-xs text-gray-600 mb-2">
            {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'].map((d) => (
              <div key={d} className="px-2 py-2 text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {segCalendarCells.map((cell, idx) => {
              const detail = cell ? (() => {
                const list = segFiltered.filter((o) => new Date(o.date).getDate() === cell.day);
                const map = new Map();
                list.forEach((o) => {
                  const u = (brigUsers || []).find((x) => String(x.id) === String(o.brigade_user_id || o.brigadaUserId || ''));
                  const raw = u?.brigada || u?.nombre || (o.brigadeId ? `Brigada ${o.brigadeId}` : '—');
                  const name = raw.replace(/^BRIGADA\s*(\d+)/i, 'BR-$1');
                  const prev = map.get(name) || { count: 0, color: getBrigadeChipColor(raw) };
                  map.set(name, { count: prev.count + 1, color: prev.color });
                });
                return Array.from(map.entries()).map(([n, v]) => ({ name: n, count: v.count, color: v.color }));
              })() : [];
              const title = detail.length ? `Brigadas: ${detail.map((x) => `${x.name}: ${x.count}`).join(', ')}` : '';
              const isSelected = cell && segSelectedDay === cell.day;
              const weekday = cell ? new Date(segYear, segMonth, cell.day).getDay() : null;
              const weekendBg = (() => {
                if (weekday === 0) return '#FEE2E2';
                if (weekday === 6) return '#F3F4F6';
                return null;
              })();
              const styleCell = cell ? { backgroundColor: isSelected ? calendarLightTheme.cellBgSelected : (weekendBg || calendarLightTheme.cellBg), borderColor: isSelected ? calendarLightTheme.cellBorderSelected : calendarLightTheme.cellBorder } : {};
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => cell && setSegSelectedDay(cell.day)}
                  className={`min-h-24 h-auto rounded border ${cell ? '' : 'border-transparent'}`}
                  style={styleCell}
                  disabled={!cell}
                  title={title}
                >
                  {cell && (
                    <div className="flex flex-col h-full">
                      <div className="px-2 pt-2 text-sm" style={{ color: calendarLightTheme.dayText }}>{cell.day}</div>
                      {detail.length > 0 && (
                        <div className="px-2 mt-1 flex flex-wrap gap-1">
                          {detail.map((x) => (
                            <span key={`${idx}-${x.name}`} className="px-2 py-0.5 rounded text-[10px] border" style={{ backgroundColor: x.color || getBrigadeChipColor(x.name), borderColor: '#D1D5DB', color: '#1F2937' }}>{x.name}: {x.count}</span>
                          ))}
                        </div>
                      )}
                      <div className="px-2 mt-auto pb-2 text-[11px] text-gray-500"></div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {segSelectedDay && (
            <div className="mt-4 border rounded p-4">
              <div className="text-sm font-semibold mb-2">Órdenes del {segSelectedDay} — {segMonthLabel}</div>
              {segDayList.length === 0 ? (
                <div className="text-sm text-gray-600">Sin órdenes para este día.</div>
              ) : (
                <ul className="divide-y">
                  {segDayList.map((o) => {
                    const u = (brigUsers || []).find((x) => String(x.id) === String(o.brigade_user_id || o.brigadaUserId || ''));
                    const ordColor = getBrigadeChipColor(u?.nombre || '');
                    return (
                      <li key={o.id} className="py-2" style={{ borderLeft: `4px solid ${ordColor}` }}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium flex items-center gap-2">
                            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: ordColor }}></span>
                            <span>{o.place || 'Sin lugar'}</span>
                            {o.execution && (<span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">Ejecución registrada</span>)}
                          </span>
                          <span className="text-sm">
                            <span className="px-2 py-0.5 rounded text-[11px] border" style={{ backgroundColor: ordColor, borderColor: '#D1D5DB', color: '#1F2937' }}>{o.time || '—:—'}</span>
                          </span>
                        </div>
                        <div className="text-sm text-gray-700">{String(o.work || '').slice(0, 120)}</div>
                        <div className="text-xs text-gray-500 mt-1">{getBrigadeLabel(o)}</div>
                        <div className="text-xs text-gray-500 mt-1">Estado: {o.status}</div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </section>
      )}
  {(!mobileMode && ['linea_viva', 'proyectos', 'gruas'].includes(coGroup) && section === 'incidencias') && (
    <section id="incidencias" className="bg-white rounded-xl shadow border border-gray-200 p-5 mt-4">
      <h4 className="text-base font-semibold mb-2">Incidencias</h4>
      <p className="text-sm text-gray-600">Gestión de incidencias y completado de órdenes.</p>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-base font-semibold">Lista de Órdenes</h5>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input type="text" placeholder="Buscar..." className="border rounded px-3 py-1 pl-8" value={incQuery} onChange={(e) => setIncQuery(e.target.value)} />
                  <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
                </div>
                <select className="border rounded px-3 py-1" value={incBrigId} onChange={(e) => setIncBrigId(e.target.value)}>
                  <option value="">Todos los usuarios</option>
                  {brigUsersForProcess.map((u) => (
                    <option key={`incbrig-${u.id}`} value={u.id}>
                      {u.brigada || u.nombre}
                    </option>
                  ))}
                </select>
                <select className="border rounded px-3 py-1" value={incStatus} onChange={(e) => setIncStatus(e.target.value)}>
                  <option value="Programado">Programado</option>
                  <option value="Todos">Todos</option>
                  <option value="Pendiente">Pendiente</option>
                  <option value="En progreso">En proceso</option>
                  <option value="Completado">Completado</option>
                  <option value="Cancelado">Cancelado</option>
                </select>
              </div>
            </div>
            {incList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Calendar size={40} className="text-gray-400" />
                <div className="mt-2 text-sm text-gray-700">No hay órdenes de trabajo registradas</div>
                <div className="text-xs text-gray-500">Crea una nueva orden para comenzar</div>
              </div>
            ) : (
              <ul className="divide-y">
                {incList.map((o) => {
                  const b = (brigades || []).find((x) => String(x.id) === String(o.brigadeId));
                  const ordColor = getBrigadeChipColor(b?.name || '');
                  return (
                    <li key={`inc-${o.id}`} className="py-3" style={{ borderLeft: `4px solid ${ordColor}` }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium flex items-center gap-2">
                            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: ordColor }}></span>
                            <span>{o.place || 'Sin lugar'}</span>
                            {o.execution && (<span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">Ejecución registrada</span>)}
                          </div>
                          <div className="text-xs text-gray-600">{o.date} {o.time || ''}</div>
                          <div className="text-xs text-gray-500 mt-1">{getBrigadeLabel(o)}</div>
                          <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                            <div>
                              <div>Supervisor cliente: {o.client_supervisor || o.clientSupervisor || '—'}</div>
                              <div>Lat/Lng: {(o.lat || '—')} / {(o.lng || '—')}</div>
                              <div>Zona/Municipio: {(o.zone || '—')} / {(o.municipality || '—')}</div>
                              <div>Solicitud: {o.requestNumber || '—'}</div>
                            </div>
                            <div>
                              <div>Clasificación: {o.activityClassification || '—'}</div>
                              <div>Trabajo: {String(o.work || '').slice(0, 120)}</div>
                            </div>
                          </div>
                          {o.execution?.node && (
                            <div className="mt-2">
                              <div className="text-xs text-gray-700">Nodo: {o.execution.node.name || '—'} • Act.: {(o.execution.node.activities || []).length} • Mat.: {(o.execution.node.materials || []).length}</div>
                              {String(o.execution.node.observations || '').trim() && (
                                <div className="text-xs text-gray-500">{String(o.execution.node.observations || '').slice(0, 140)}</div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            className="text-xs border rounded px-2 py-1 bg-white"
                            value={o.status}
                            onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                          >
                            <option value="Pendiente">Pendiente</option>
                            <option value="Programado">Programado</option>
                            <option value="En progreso">En proceso</option>
                            <option value="Completado">Completado</option>
                            <option value="Cancelado">Cancelado</option>
                          </select>
                          <button type="button" onClick={() => openComplete(o)} className="px-2 py-1 text-xs rounded bg-blue-600 text-white">Completar</button>
                          <button type="button" onClick={() => openReprogram(o)} className="px-2 py-1 text-xs rounded" style={{ backgroundColor: '#FDE68A', color: '#1F2937' }}>Reprogramar</button>
                          <button type="button" onClick={() => openDeleteOrder(o)} className="px-2 py-1 text-xs rounded border">Eliminar</button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      )}
      {(!mobileMode && ['linea_viva', 'proyectos', 'gruas'].includes(coGroup) && section === 'reportes') && (
        <section id="reportes" className="bg-white rounded-xl shadow border border-gray-200 p-5 mt-4">
          <h4 className="text-base font-semibold mb-2">Reportes</h4>
          <div className="mt-2">
            <h5 className="text-base font-semibold">Gestión de Brigadas</h5>
            <p className="text-sm text-gray-600 mb-3">Órdenes por brigada.</p>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <select className="border rounded px-3 py-1" value={repStatus} onChange={(e) => setRepStatus(e.target.value)}>
                  <option value="Programado">Programado</option>
                  <option value="Todos">Todos</option>
                  <option value="Pendiente">Pendiente</option>
                  <option value="En progreso">En proceso</option>
                  <option value="Completado">Completado</option>
                  <option value="Cancelado">Cancelado</option>
                </select>
                <select className="border rounded px-3 py-1" value={repLocation} onChange={(e) => setRepLocation(e.target.value)}>
                  <option value="Actual">Sede actual</option>
                  <option value="Todas">Todas las sedes</option>
                </select>
                <select className="border rounded px-3 py-1" value={repBrigId} onChange={(e) => setRepBrigId(e.target.value)}>
                  <option value="">Todas las brigadas</option>
                  {brigUsersForProcess.map((u) => (
                    <option key={`repbrig-${u.id}`} value={u.id}>{u.brigada || u.nombre}</option>
                  ))}
                </select>
                <input type="date" className="border rounded px-3 py-1" value={repStartDate} onChange={(e) => setRepStartDate(e.target.value)} />
                <input type="date" className="border rounded px-3 py-1" value={repEndDate} onChange={(e) => setRepEndDate(e.target.value)} />
                <button type="button" onClick={exportReport} className="px-3 py-1 text-sm rounded-md bg-blue-600 text-white">Exportar Excel</button>
                <button type="button" onClick={exportExecutionReport} className="px-3 py-1 text-sm rounded-md text-white" style={{ backgroundColor: '#776F74' }}>Exportar Ejecución</button>
              </div>
              <div className="text-xs text-gray-600">Total: {repTotal} ordenes</div>
            </div>
            {repBrigades.length === 0 ? (
              <div className="text-sm text-gray-600">No hay brigadas configuradas.</div>
            ) : (
              <ul className="space-y-3">
                {repBrigades.map(({ b, count, respName, respId }) => (
                  <li key={`repb-${b.id}`} className="border rounded-lg p-3 flex items-center justify-between" style={{ borderLeft: `4px solid ${brigadeColor(b)}` }}>
                    <div>
                      <div className="text-sm font-semibold flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: brigadeColor(b) }}></span>
                        <span>{b.name}</span>
                      </div>
                      <div className="text-xs text-gray-600">Responsable: {respName ? `${respName} (${respId})` : (respId || '—')}</div>
                    </div>
                    <div className="text-sm text-gray-800">{count} ordenes</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {(!mobileMode && coGroup === 'administracion' && section === 'configuracion') && (
        <section id="configuracion" className="bg-white rounded-xl shadow border border-gray-200 p-5 mt-4">
          <h4 className="text-base font-semibold mb-2">Configuración</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <div className="text-sm font-semibold mb-2">Crear/Configurar actividad</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <input type="text" placeholder="Código (Ej. S-001)" className="border rounded px-3 py-2" value={actForm.name} onChange={(e) => setActForm({ ...actForm, name: e.target.value })} />
                  <input type="text" placeholder="Descripción del código" className="border rounded px-3 py-2" value={actForm.description} onChange={(e) => setActForm({ ...actForm, description: e.target.value })} />
                  <select className="border rounded px-3 py-2 bg-white" value={actForm.process_type} onChange={(e) => setActForm({ ...actForm, process_type: e.target.value })}>
                    <option value="linea_viva">Línea viva</option>
                    <option value="proyectos">Proyectos</option>
                    <option value="gruas">Gruas</option>
                    <option value="alumbrado_publico">Alumbrado público</option>
                  </select>
                  <div className="flex items-center border rounded bg-white overflow-hidden">
                    <span className="px-3 text-gray-500 select-none">$</span>
                    <input type="number" step="0.01" placeholder="Valor unitario" className="flex-1 px-3 py-2 outline-none" value={actForm.valor_unitario} onChange={(e) => setActForm({ ...actForm, valor_unitario: e.target.value })} />
                  </div>
                </div>
                <div className="mt-3">
                  <button type="button" onClick={addConfigAct} className="px-3 py-1 text-sm rounded-md bg-blue-600 text-white inline-flex items-center gap-1">
                    {actEditId ? <Pencil size={16} /> : <Plus size={16} />}
                    {actEditId ? 'Actualizar actividad' : 'Guardar actividad'}
                  </button>
                </div>
              </div>

              <div className="mt-4">
            <div className="text-sm font-semibold mb-2">Actividades configuradas</div>
            {configActs.length === 0 ? (
              <div className="text-sm text-gray-600">No hay actividades configuradas.</div>
            ) : (
              <div className="border rounded-lg overflow-x-auto bg-white">
                <div className="min-w-[760px] px-3 py-2 bg-gray-50 border-b text-[11px] font-semibold tracking-widest text-gray-500 grid grid-cols-12 gap-2">
                  <div className="col-span-3">CÓDIGO</div>
                  <div className="col-span-4">DESCRIPCIÓN</div>
                  <div className="col-span-2">PROCESO</div>
                  <div className="col-span-1 text-right">VALOR</div>
                  <div className="col-span-2 text-right">ACCION</div>
                </div>
                <ul className="divide-y min-w-[760px]">
                  {configActs.map((a) => (
                    <li key={`act-${a.id}`} className="px-3 py-2 grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-3 font-semibold text-sm text-gray-900 truncate">{a.name || '—'}</div>
                      <div className="col-span-4 text-sm text-gray-600 truncate">{a.description || '—'}</div>
                      <div className="col-span-2 text-sm text-gray-700 truncate">{processLabel(a.process_type || a.tipo_proceso)}</div>
                      <div className="col-span-1 text-right text-sm font-semibold text-gray-900">{formatCOP(a.valor_unitario)}</div>
                      <div className="col-span-2 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setActEditId(String(a.id));
                            setActForm({
                              name: String(a.name || ''),
                              description: String(a.description || ''),
                              process_type: String(a.process_type || a.tipo_proceso || 'linea_viva'),
                              valor_unitario: String(a.valor_unitario ?? ''),
                            });
                          }}
                          className="text-gray-700 hover:text-gray-900 inline-flex items-center gap-1 text-sm"
                        >
                          <Pencil size={16} /> Editar
                        </button>
                        <button type="button" onClick={() => removeConfigAct(a.id)} className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-sm">
                          <Trash size={16} /> Eliminar
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
            </div>
          </div>
        </section>
      )}
      {(!mobileMode && coGroup === 'administracion' && section === 'brigada') && (
        <section id="brigada" className="bg-white rounded-xl shadow border border-gray-200 p-5 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-base font-semibold">Usuarios de Brigada (App)</h4>
          </div>
            {brigUsersMessage && (
              <div className={`p-3 mb-4 rounded-lg flex items-center gap-2 text-sm ${brigUsersMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {brigUsersMessage.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                <span>{brigUsersMessage.text}</span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Buscar por Cédula</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={brigUserCedulaSearch}
                      onChange={(e) => setBrigUserCedulaSearch(e.target.value)}
                      placeholder="Ingrese número de documento"
                      className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                    <button
                      type="button"
                      onClick={searchBrigUserPerson}
                      disabled={brigUsersLoading}
                      className="bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2 text-sm"
                    >
                      <Search size={16} /> Buscar
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Busca la información básica en la base de datos de personal.</p>
                </div>
                <form onSubmit={handleCreateBrigUser} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cédula</label>
                      <input
                        type="text"
                        value={brigUserForm.cedula}
                        onChange={(e) => setBrigUserForm({ ...brigUserForm, cedula: e.target.value })}
                        className="w-full p-2 border rounded-lg bg-gray-50"
                        readOnly={!!brigUserFoundPerson}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo</label>
                      <input
                        type="text"
                        value={brigUserForm.nombre}
                        onChange={(e) => setBrigUserForm({ ...brigUserForm, nombre: e.target.value })}
                        className="w-full p-2 border rounded-lg"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 text-gray-400" size={18} />
                      <input
                        type="text"
                        value={brigUserForm.usuario}
                        onChange={(e) => setBrigUserForm({ ...brigUserForm, usuario: e.target.value })}
                        className="w-full pl-10 p-2 border rounded-lg"
                        placeholder="Cédula o usuario"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                    <div className="relative">
                      <Key className="absolute left-3 top-2.5 text-gray-400" size={18} />
                      <input
                        type="password"
                        value={brigUserForm.password}
                        onChange={(e) => setBrigUserForm({ ...brigUserForm, password: e.target.value })}
                        className="w-full pl-10 p-2 border rounded-lg"
                        placeholder={brigUserEditId ? "Dejar vacío para no cambiar" : "Establecer contraseña"}
                        required={!brigUserEditId}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Brigada</label>
                    <select
                      className="w-full p-2 border rounded-lg bg-white"
                      value={brigUserForm.brigada}
                      onChange={(e) => setBrigUserForm({ ...brigUserForm, brigada: e.target.value })}
                    >
                      <option value="">Seleccionar brigada (opcional)</option>
                      {[1,2,3,4,5,6,7,8,9,10].map((num) => (
                        <option key={`brig-fixed-${num}`} value={`BRIGADA${num}`}>{`BRIGADA${num}`}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de proceso</label>
                    <select
                      className="w-full p-2 border rounded-lg bg-white"
                      value={brigUserForm.tipo_proceso}
                      onChange={(e) => setBrigUserForm({ ...brigUserForm, tipo_proceso: e.target.value })}
                      required
                    >
                      <option value="">Seleccionar tipo de proceso</option>
                      <option value="linea_viva">Línea viva</option>
                      <option value="proyectos">Proyectos</option>
                      <option value="gruas">Gruas</option>
                      <option value="alumbrado_publico">Alumbrado público</option>
                    </select>
                  </div>
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={brigUsersLoading}
                      className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 flex justify-center items-center gap-2 text-sm"
                    >
                      <Save size={16} />
                      {brigUsersLoading ? 'Guardando...' : (brigUserEditId ? 'Actualizar usuario de brigada' : 'Guardar usuario de brigada')}
                    </button>
                    {brigUserEditId ? (
                      <button
                        type="button"
                        onClick={cancelEditBrigUser}
                        disabled={brigUsersLoading}
                        className="w-full mt-2 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 text-sm"
                      >
                        Cancelar edición
                      </button>
                    ) : null}
                  </div>
                </form>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4 overflow-x-auto">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-sm font-semibold text-gray-800">Usuarios registrados</h5>
                  {brigUsersLoading && <span className="text-xs text-gray-500">Cargando...</span>}
                </div>
                {brigUsersLoading ? (
                  <div className="text-sm text-gray-500 py-6 text-center">Cargando usuarios de brigada...</div>
                ) : brigUsers.length === 0 ? (
                  <div className="text-sm text-gray-500 py-6 text-center">No hay usuarios de brigada registrados.</div>
                ) : (
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="p-2 font-medium text-gray-600">Nombre</th>
                        <th className="p-2 font-medium text-gray-600">Cédula</th>
                        <th className="p-2 font-medium text-gray-600">Usuario</th>
                        <th className="p-2 font-medium text-gray-600">Brigada</th>
                        <th className="p-2 font-medium text-gray-600">Proceso</th>
                        <th className="p-2 font-medium text-gray-600">Fecha</th>
                        <th className="p-2 font-medium text-gray-600 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brigUsers.map((u) => (
                        <tr key={`briguser-${u.id}`} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="p-2 text-gray-800 flex items-center gap-2">
                            <User size={16} className="text-gray-400" />
                            {u.nombre}
                          </td>
                          <td className="p-2 text-gray-600">{u.cedula || '-'}</td>
                          <td className="p-2 text-gray-600">{u.usuario}</td>
                          <td className="p-2 text-gray-600">{u.brigada || '-'}</td>
                          <td className="p-2 text-gray-600">{processTypeLabel(u.tipo_proceso)}</td>
                          <td className="p-2 text-gray-500 text-xs">{u.created_at || ''}</td>
                          <td className="p-2 text-right">
                            <button
                              type="button"
                              onClick={() => startEditBrigUser(u)}
                              className="text-blue-600 hover:bg-blue-50 p-1 rounded-full mr-1"
                              title="Editar usuario"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteBrigUser(u.id)}
                              className="text-red-500 hover:bg-red-50 p-1 rounded-full"
                              title="Eliminar usuario"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
        </section>
      )}
      
      {(!mobileMode && ['linea_viva', 'proyectos', 'gruas'].includes(coGroup) && section === 'indicadores') && (
        <section id="indicadores" className="bg-white rounded-xl shadow border border-gray-200 p-5 mt-4">
           <ControlOperativoDashboard orders={segOrdersForProcess} brigades={brigUsersForProcess} />
        </section>
      )}

      {mobileMode && (
        <section id="movil" className="mt-0">
          <div className="px-4 pt-4 pb-16">
            {/* Selector de brigada oculto en APK: se determina por el usuario autenticado */}
            {mobileTab === 'inicio' && (
              <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #EFF6FF 0%, #FFFFFF 100%)' }}>
                <div className="rounded-2xl text-white p-4 mb-3" style={{ backgroundColor: '#776F74' }}>
                  <div className="text-2xl font-bold">Órdenes de Trabajo</div>
                  <div className="text-xs opacity-90">Bienvenido</div>
                </div>
                <div className="my-3 flex items-center justify-center">
                  <button
                    type="button"
                    onClick={syncMaterials}
                    className="rounded-2xl shadow-xl hover:shadow-2xl transition-shadow px-6 py-4 inline-flex flex-col items-center gap-2"
                    style={{ backgroundColor: '#D7ECFF', boxShadow: syncPressed ? '0 32px 80px rgba(107, 114, 128, 0.55)' : '0 24px 60px rgba(156, 163, 175, 0.45)' }}
                    onMouseDown={() => setSyncPressed(true)}
                    onMouseUp={() => setSyncPressed(false)}
                    onMouseLeave={() => setSyncPressed(false)}
                    onTouchStart={() => setSyncPressed(true)}
                    onTouchEnd={() => setSyncPressed(false)}
                    aria-label="Sincronizar materiales"
                  >
                    <div className="flex items-center justify-center" style={{ width: 96, height: 72 }}>
                      <img src={sincronizarIcon} alt="Sincronizar" style={{ width: 96, height: 72 }} />
                    </div>
                    <div className="text-xs text-gray-700">{syncMsg || 'Sincronizar'}</div>
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-2 rounded-lg flex flex-col items-center text-center shadow-md hover:shadow-lg transition-shadow" style={{ backgroundColor: '#FFE8D2', boxShadow: '0 24px 60px rgba(156, 163, 175, 0.45)' }}>
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: '#FFE3B3' }}>
                      <img src={pendienteIcon} alt="Pendiente" style={{ width: 36, height: 36 }} />
                    </div>
                    <div className="text-xl font-bold">{mobileOrders.filter((o) => String(o.status || '').toLowerCase().includes('program')).length}</div>
                    <div className="text-xs text-gray-700">Pendientes</div>
                  </div>
                  <div className="p-2 rounded-lg flex flex-col items-center text-center shadow-md hover:shadow-lg transition-shadow" style={{ backgroundColor: '#E5E7EB', boxShadow: '0 24px 60px rgba(156, 163, 175, 0.45)' }}>
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: '#F3F4F6' }}>
                      <img src={enProcesoIcon} alt="En proceso" style={{ width: 36, height: 36 }} />
                    </div>
                    <div className="text-xl font-bold">{mobileOrders.filter((o) => (String(o.status || '').toLowerCase().includes('progreso') || String(o.status || '').toLowerCase().includes('curso'))).length}</div>
                    <div className="text-xs text-gray-700">En proceso</div>
                  </div>
                  <div className="p-2 rounded-lg flex flex-col items-center text-center shadow-md hover:shadow-lg transition-shadow" style={{ backgroundColor: '#D8F5D6', boxShadow: '0 24px 60px rgba(156, 163, 175, 0.45)' }}>
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: '#CDEEE0' }}>
                      <img src={completadaIcon} alt="Completadas" style={{ width: 36, height: 36 }} />
                    </div>
                    <div className="text-xl font-bold">{mobileOrders.filter((o) => String(o.status || '').toLowerCase().includes('comple')).length}</div>
                    <div className="text-xs text-gray-700">Completadas</div>
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  <button type="button" onClick={() => setMobileTab('ordenes')} className="w-full px-4 py-3 rounded-md text-white inline-flex items-center justify-center gap-2" style={{ backgroundColor: '#776F74' }}><ClipboardList size={18} /> Ver Órdenes</button>
                </div>
                <div className="mt-5">
                  <div className="text-sm font-semibold mb-2">Órdenes Recientes</div>
                  <ul className="space-y-3">
                    {(() => {
                      const toTs = (d, t) => {
                        try { return new Date(`${d || ''}T${t || '00:00'}`).getTime() || 0; } catch { return 0; }
                      };
                      const list = [...mobileOrders]
                        .sort((a, b) => toTs(b.date, b.time) - toTs(a.date, a.time))
                        .slice(0, 5);
                      return list.map((o) => {
                        const v = String(o.status || '').toLowerCase();
                        const mapped = v.includes('program') ? 'pendiente' : (v.includes('curso') || v.includes('progreso')) ? 'en progreso' : v.includes('comple') ? 'completado' : 'pendiente';
                        const badgeStyle = mapped === 'pendiente' ? { backgroundColor: '#FEF3C7', color: '#92400E' } : mapped === 'en progreso' ? { backgroundColor: '#EDEBED', color: '#776F74' } : { backgroundColor: '#D1FAE5', color: '#065F46' };
                        const emp = (employees || []).find((e) => {
                          const rid = String(o?.responsibleId || '');
                          return String(e.id || '') === rid || String(e.cedula || e.documento_identidad || e.dni || e.documento || e.doc || '') === rid;
                        });
                        const nombre = emp?.name || emp?.nombre_completo || '';
                        const fdate = (() => { try { return new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(o.date)); } catch { return o.date || ''; } })();
                        return (
                          <li
                            key={`rec-${o.id}`}
                            className={`rounded-xl border p-3 shadow-sm ${mapped === 'pendiente' || mapped === 'en progreso' ? 'cursor-pointer hover:bg-gray-50' : 'opacity-80'}`}
                            onClick={() => { if (mapped === 'pendiente' || mapped === 'en progreso') openComplete(o); }}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="text-sm font-semibold">{o.place || 'Sin lugar'}</div>
                                <div className="mt-1 flex items-center gap-3 text-xs text-gray-700">
                                  <span className="inline-flex items-center gap-1"><Calendar size={14} /> {fdate}</span>
                                  <span className="inline-flex items-center gap-1"><Clock size={14} /> {o.time || ''}</span>
                                </div>
                                <div className="mt-1 text-xs text-gray-700">{nombre || '—'}</div>
                              </div>
                              <span className="text-xs px-2 py-1 rounded-full" style={badgeStyle}>{mapped === 'en progreso' ? 'en proceso' : mapped}</span>
                            </div>
                          </li>
                        );
                      });
                    })()}
                  </ul>
                </div>
              </div>
            )}
            {mobileTab === 'ordenes' && (
              <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #F8FAFF 0%, #FFFFFF 100%)' }}>
                <div className="rounded-2xl text-white p-4 mb-3" style={{ backgroundColor: '#776F74' }}>
                  <div className="text-2xl font-bold">Mis Órdenes</div>
                    <div className="text-xs opacity-90">{mobileOrders.length} órdenes totales</div>
                </div>
                <div className="flex items-center justify-end mb-2">
                  <button type="button" onClick={syncMaterials} className="px-3 py-1 rounded-md border inline-flex items-center gap-1 shadow-md hover:shadow-lg transition-shadow" style={{ backgroundColor: '#E0F2FE', borderColor: '#93C5FD' }}><RefreshCcw size={16} /> Sincronizar</button>
                </div>
                {!mobileBrigId ? (
                  <div className="text-xs text-gray-600">Selecciona una brigada para ver sus órdenes.</div>
                ) : (
                  <ul className="space-y-3">
                    {(() => {
                      const toTs = (d, t) => {
                        try { return new Date(`${d || ''}T${t || '00:00'}`).getTime() || 0; } catch { return 0; }
                      };
                      const list = [...mobileOrders]
                        .sort((a, b) => toTs(b.date, b.time) - toTs(a.date, a.time));
                      return list.map((o) => {
                        const v = String(o.status || '').toLowerCase();
                        const mapped = v.includes('program') ? 'pendiente' : (v.includes('curso') || v.includes('progreso')) ? 'en progreso' : v.includes('comple') ? 'completado' : 'pendiente';
                        const badgeStyle = mapped === 'pendiente' ? { backgroundColor: '#FEF3C7', color: '#92400E' } : mapped === 'en progreso' ? { backgroundColor: '#EDEBED', color: '#776F74' } : { backgroundColor: '#D1FAE5', color: '#065F46' };
                        const emp = (employees || []).find((e) => {
                          const rid = String(o?.responsibleId || '');
                          return String(e.id || '') === rid || String(e.cedula || e.documento_identidad || e.dni || e.documento || e.doc || '') === rid;
                        });
                        const nombre = emp?.name || emp?.nombre_completo || '';
                        const titulo = o.work || o.place || 'Sin título';
                        const fdate2 = (() => { try { return new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(o.date)); } catch { return o.date || ''; } })();
                        return (
                          <li
                            key={`ord-${o.id}`}
                            className={`rounded-lg border p-3 shadow-sm ${mapped === 'pendiente' || mapped === 'en progreso' ? 'cursor-pointer hover:bg-gray-50' : 'opacity-80'}`}
                            onClick={() => { if (mapped === 'pendiente' || mapped === 'en progreso') openComplete(o); }}
                          >
                            <div className="flex items-start justify-between">
                              <div className="text-sm font-semibold">{titulo}</div>
                              <span className="text-xs px-2 py-1 rounded-full" style={badgeStyle}>{mapped === 'en progreso' ? 'en proceso' : mapped}</span>
                            </div>
                            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-700">
                              <div className="inline-flex items-center gap-1"><Calendar size={14} /> {fdate2}</div>
                              <div className="inline-flex items-center gap-1"><Clock size={14} /> {o.time || ''}</div>
                              <div className="inline-flex items-center gap-1"><User size={14} /> {nombre || '—'}</div>
                              <div className="inline-flex items-center gap-1"><MapPin size={14} /> {o.place || 'Sin lugar'}</div>
                            </div>
                          </li>
                        );
                      });
                    })()}
                  </ul>
                )}
              </div>
            )}
            {mobileTab === 'mapa' && (
              <div>
                <div className="rounded-2xl text-white p-4 mb-3" style={{ backgroundColor: '#776F74' }}>
                  <div className="text-2xl font-bold">Mapa</div>
                </div>
                {(() => {
                  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || window.__gmapsKey || '';
                  const listProg = mobileOrders.filter((o) => String(o.status || '').toLowerCase().includes('program'));
                  const withCoords = listProg.filter((o) => o.lat && o.lng);
                  const center = withCoords.length > 0
                    ? { lat: parseFloat(withCoords[0].lat), lng: parseFloat(withCoords[0].lng) }
                    : (mapLoc ? { lat: mapLoc.lat, lng: mapLoc.lng } : { lat: 4.710989, lng: -74.072090 });
                  if (key && withCoords.length > 0) return <MapView items={withCoords} />;
                  const markers = withCoords.slice(0, 10).map((o) => ({ lat: parseFloat(o.lat), lng: parseFloat(o.lng) }));
                  return (
                    <div className="rounded-2xl border overflow-hidden mb-3" style={{ backgroundColor: '#F8FAFF' }}>
                      <OSMEmbed center={center} markers={markers} />
                    </div>
                  );
                })()}
              </div>
            )}
            {mobileTab === 'camara' && (
              <div>
                <div className="rounded-2xl text-white p-4 mb-3" style={{ backgroundColor: '#776F74' }}>
                  <div className="text-2xl font-bold">Registro fotográfico</div>
                </div>
                {mobileMode && (
                  <div className="space-y-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Seleccionar Orden</label>
                      <select className="w-full border rounded px-3 py-2" value={selectedCameraOrderId} onChange={(e) => setSelectedCameraOrderId(e.target.value)}>
                        <option value="">-- Selecciona una orden --</option>
                        {mobileOrders.filter((o) => String(o.status || '').toLowerCase().includes('program')).map((o) => (
                          <option key={`camorder-${o.id}`} value={o.id}>{o.id} - {(o.place || o.work || 'Sin título')}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" disabled={!selectedCameraOrderId} onClick={() => {
                        const n = cameraNodes.length + 1;
                        const node = { id: n, label: `Nodo ${n}`, photos: { antes: [], durante: [], despues: [] } };
                        setCameraNodes((prev) => [...prev, node]);
                        setActiveNodeIndex(n - 1);
                      }} className="px-3 py-2 rounded-md text-white shadow-md" style={{ backgroundColor: '#2563eb', opacity: selectedCameraOrderId ? 1 : 0.6 }}>Agregar Nodo</button>
                      {cameraNodes.length > 0 && (
                        <div className="text-xs px-2 py-1 rounded border" style={{ borderColor: '#F59E0B', color: '#92400E', backgroundColor: '#FEF3C7' }}>Nodos creados ({cameraNodes.length})</div>
                      )}
                    </div>
                    {cameraNodes.length > 0 && (
                      <div className="space-y-3">
                        <div className="rounded border p-3" style={{ borderColor: '#FEE2E2', backgroundColor: '#FFF7ED' }}>
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold">{cameraNodes[activeNodeIndex]?.label || ''}</div>
                            <div className="text-xs">{(['antes','durante','despues'].reduce((s,k)=>s+(cameraNodes[activeNodeIndex]?.photos?.[k]?.length||0),0))} fotos</div>
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-xs">
                            <span className="px-2 py-1 rounded" style={{ backgroundColor: '#bfdbfe', color: '#1e3a8a' }}>A: {cameraNodes[activeNodeIndex]?.photos?.antes?.length||0}</span>
                            <span className="px-2 py-1 rounded" style={{ backgroundColor: '#fef9c3', color: '#92400E' }}>D: {cameraNodes[activeNodeIndex]?.photos?.durante?.length||0}</span>
                            <span className="px-2 py-1 rounded" style={{ backgroundColor: '#d1fae5', color: '#065F46' }}>De: {cameraNodes[activeNodeIndex]?.photos?.despues?.length||0}</span>
                          </div>
                        </div>
                        <div className="rounded-2xl border p-3" style={{ backgroundColor: '#F3F4F6' }}>
                          <div className="text-sm font-semibold mb-2">Capturando: {selectedCameraOrderId ? `${selectedCameraOrderId}-NODO-${activeNodeIndex+1}` : ''}</div>
                          <div className="flex items-center gap-2 mb-3 text-xs">
                            <button type="button" onClick={() => setCameraStage('antes')} className="px-3 py-1 rounded" style={{ backgroundColor: cameraStage==='antes'?'#93c5fd':'#e5e7eb' }}>Antes ({cameraNodes[activeNodeIndex]?.photos?.antes?.length||0})</button>
                            <button type="button" onClick={() => setCameraStage('durante')} className="px-3 py-1 rounded" style={{ backgroundColor: cameraStage==='durante'?'#fde68a':'#e5e7eb' }}>Durante ({cameraNodes[activeNodeIndex]?.photos?.durante?.length||0})</button>
                            <button type="button" onClick={() => setCameraStage('despues')} className="px-3 py-1 rounded" style={{ backgroundColor: cameraStage==='despues'?'#86efac':'#e5e7eb' }}>Después ({cameraNodes[activeNodeIndex]?.photos?.despues?.length||0})</button>
                          </div>
                          {(() => {
                            const nowTxt = (() => { try { return new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date()); } catch { return '';} })();
                            return (
                              <div className="rounded-lg p-3" style={{ backgroundColor: '#0f0f0f', color: '#fff' }}>
                                <div className="flex items-center justify-between text-xs mb-2">
                                  <span className="px-2 py-1 rounded" style={{ backgroundColor: '#F07E2D' }}>SENERFI SAS</span>
                                  <span className="px-2 py-1 rounded" style={{ backgroundColor: '#1f2937' }}>{cameraStage==='antes'?'Antes':cameraStage==='durante'?'Durante':'Después'}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <div>{selectedCameraOrderId ? `${selectedCameraOrderId}-NODO-${activeNodeIndex+1}` : ''}</div>
                                  <div>{nowTxt}</div>
                                </div>
                                <div className="text-xs mt-1" style={{ color: '#fca5a5' }}>{cameraLoc ? `${cameraLoc.lat?.toFixed(6)}, ${cameraLoc.lng?.toFixed(6)}` : 'Ubicación no disponible'}</div>
                                <div className="mt-3 flex items-center gap-2">
                                  <button type="button" onClick={() => takePhotoForStage('antes')} className="px-3 py-2 rounded text-white w-full" style={{ backgroundColor: '#3b82f6' }}>Tomar Antes</button>
                                  <button type="button" onClick={() => takePhotoForStage('durante')} className="px-3 py-2 rounded text-white w-full" style={{ backgroundColor: '#f59e0b' }}>Tomar Durante</button>
                                  <button type="button" onClick={() => takePhotoForStage('despues')} className="px-3 py-2 rounded text-white w-full" style={{ backgroundColor: '#10b981' }}>Tomar Después</button>
                                  <input ref={cameraFileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => {
                                    try {
                                      const file = e.target.files && e.target.files[0];
                                      if (!file) return;
                                      const reader = new FileReader();
                                      reader.onload = async (ev) => {
                                        const dataUrl = ev.target?.result;
                                        if (typeof dataUrl !== 'string') return;
                                        const processed = await renderNodeOverlay(dataUrl);
                                        setCameraNodes((prev) => {
                                          const list = [...prev];
                                          const node = list[activeNodeIndex];
                                          if (!node) return prev;
                                          const ph = node.photos[cameraStage] || [];
                                          node.photos[cameraStage] = [processed, ...ph].slice(0, 24);
                                          list[activeNodeIndex] = { ...node };
                                          return list;
                                        });
                                      };
                                      reader.readAsDataURL(file);
                                    } catch {}
                                  }} />
                                  <canvas ref={cameraCanvasRef} className="hidden" />
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        <div>
                          <div className="text-sm font-semibold mb-2">Galería del {cameraNodes[activeNodeIndex]?.label || ''}</div>
                          <div className="grid grid-cols-2 gap-2">
                            {(['antes','durante','despues'].flatMap((k) => cameraNodes[activeNodeIndex]?.photos?.[k] || [])).map((u, i) => (
                              <div key={`camnode-shot-${i}`} className="border rounded overflow-hidden">
                                <img src={u} alt="shot" className="w-full h-40 object-cover" />
                              </div>
                            ))}
                            {(['antes','durante','despues'].reduce((s,k)=>s+(cameraNodes[activeNodeIndex]?.photos?.[k]?.length||0),0))===0 && (
                              <div className="text-xs text-gray-600">Sin fotos</div>
                            )}
                          </div>
                        </div>
                        <div className="rounded border p-3 text-sm" style={{ backgroundColor: '#F3F4F6' }}>
                          <div>Resumen de Captura</div>
                          <div>Orden: {selectedCameraOrderId || '—'}</div>
                          <div>Total de nodos: {cameraNodes.length}</div>
                          <div>Total de fotos: {cameraNodes.reduce((s,n)=>s+(['antes','durante','despues'].reduce((t,k)=>t+(n.photos[k]?.length||0),0)),0)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {!mobileMode && (() => {
                  const u = (brigUsers || []).find((x) => String(x.id) === String(mobileBrigId));
                  const ced = u?.cedula || u?.usuario || '';
                  return <CameraTab brigadeName={u?.nombre || ''} cedula={ced} />;
                })()}
              </div>
            )}
            
          </div>
          <div className="fixed bottom-0 left-0 right-0 border-t bg-gray-100">
            <div className="grid grid-cols-4 text-center text-xs">
              <button type="button" onClick={() => setMobileTab('inicio')} className="py-2 flex flex-col items-center" style={{ color: mobileTab==='inicio' ? '#776F74' : '#6b7280' }}>
                <Home size={18} />
                <span className="mt-1">Inicio</span>
              </button>
              <button type="button" onClick={() => setMobileTab('ordenes')} className="py-2 flex flex-col items-center" style={{ color: mobileTab==='ordenes' ? '#776F74' : '#6b7280' }}>
                <ClipboardList size={18} />
                <span className="mt-1">Órdenes</span>
              </button>
              <button type="button" onClick={() => setMobileTab('mapa')} className="py-2 flex flex-col items-center" style={{ color: mobileTab==='mapa' ? '#776F74' : '#6b7280' }}>
                <MapPin size={18} />
                <span className="mt-1">Mapa</span>
              </button>
              <button type="button" onClick={() => setMobileTab('camara')} className="py-2 flex flex-col items-center" style={{ color: mobileTab==='camara' ? '#776F74' : '#6b7280' }}>
                <Camera size={18} />
                <span className="mt-1">Cámara</span>
              </button>
            </div>
          </div>
        </section>
      )}

      <Modal isOpen={isOrderOpen} onClose={() => setIsOrderOpen(false)} title="Nueva Orden de Trabajo" size="max-w-3xl">
        <form onSubmit={saveOrder} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Brigada (Usuario) *</label>
            <select
              className="w-full border rounded px-3 py-2 bg-white"
              value={currentBrigadeValue}
              onChange={handleBrigadeSelect}
              required
            >
              <option value="">Seleccionar brigada</option>
              {brigadeOptions.map((opt) => (
                <option key={`${opt.type}-${opt.id}`} value={`${opt.type}-${opt.id}`}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Fecha *</label>
              <input type="date" className="w-full border rounded px-3 py-2" value={order.date} onChange={(e) => setOrder({ ...order, date: e.target.value })} required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hora</label>
              <input type="time" className="w-full border rounded px-3 py-2" value={order.time} onChange={(e) => setOrder({ ...order, time: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado</label>
              <input type="text" className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-500" value="Programado" disabled />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Lugar</label>
              <input
                type="text"
                placeholder="Ej: Calle 123 #45-67, Bogotá"
                className="w-full border rounded px-3 py-2"
                value={order.place}
                onChange={(e) => setOrder({ ...order, place: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Supervisor Senerfi</label>
              <input type="text" className="w-full border rounded px-3 py-2" value={order.requestNumber} onChange={(e) => setOrder({ ...order, requestNumber: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Supervisor cliente</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={order.clientSupervisor || ''}
                onChange={(e) => setOrder({ ...order, clientSupervisor: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Clasificación actividad</label>
              <select className="w-full border rounded px-3 py-2 bg-white" value={order.activityClassification} onChange={(e) => setOrder({ ...order, activityClassification: e.target.value })}>
                <option value="">Seleccionar</option>
                <option>Emergencia</option>
                <option>Acompañamiento y/o apoyo</option>
                <option>Programadas</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Zona</label>
              <input type="text" className="w-full border rounded px-3 py-2" value={order.zone} onChange={(e) => setOrder({ ...order, zone: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Municipio</label>
              <input type="text" className="w-full border rounded px-3 py-2" value={order.municipality} onChange={(e) => setOrder({ ...order, municipality: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Latitud</label>
              <input type="text" className="w-full border rounded px-3 py-2" value={order.lat} onChange={(e) => setOrder({ ...order, lat: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Longitud</label>
              <input type="text" className="w-full border rounded px-3 py-2" value={order.lng} onChange={(e) => setOrder({ ...order, lng: e.target.value })} />
            </div>
          </div>

          

          <div>
            <label className="block text-sm font-medium mb-1">Descripción del trabajo *</label>
            <textarea className="w-full border rounded px-3 py-2 min-h-[60px]" value={order.work} onChange={(e) => setOrder({ ...order, work: e.target.value })} required />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notas Adicionales</label>
            <textarea className="w-full border rounded px-3 py-2 min-h-[80px]" value={order.notes} onChange={(e) => setOrder({ ...order, notes: e.target.value })} />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => setIsOrderOpen(false)} className="px-4 py-2 rounded bg-gray-200 text-gray-800">Cancelar</button>
            <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white inline-flex items-center gap-2"><Save size={16} /> Guardar Orden</button>
          </div>
      </form>
      </Modal>
      <Modal isOpen={isCompleteOpen} onClose={() => setIsCompleteOpen(false)} title="Completar Orden" size="max-w-3xl">
        <form onSubmit={saveComplete} className="space-y-4">
          {completeMsg && (<p className="text-xs text-red-600">{completeMsg}</p>)}
          <div className="bg-gray-50 border rounded p-3">
            <div className="text-sm font-semibold mb-2">Programación</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-700">
              <div>
                {(() => {
                  const o = segOrders.find((x) => x.id === editingOrderId) || {};
                  const u = (brigUsers || []).find((x) => String(x.id) === String(o.brigade_user_id || o.brigadaUserId || ''));
                  const code = u?.brigada || '';
                  const respName = u?.nombre || '';
                  const respId = u?.cedula || u?.usuario || '';
                  const brigadeText = code || 'Sin brigada';
                  const respText = respName ? `${respName}${respId ? ' (' + respId + ')' : ''}` : respId || '—';
                  return (
                    <div>
                      <div className="font-bold text-blue-800 mb-1">Orden #: {o.id}</div>
                      <div>Brigada: {brigadeText}</div>
                      <div>Responsable: {respText}</div>
                      <div>Clasificación: {o.activityClassification || '—'}</div>
                      <div>Fecha/Hora: {o.date || '—'} {o.time || ''}</div>
                    </div>
                  );
                })()}
              </div>
              <div>
                {(() => {
                  const o = segOrders.find((x) => x.id === editingOrderId) || {};
                  return (
                    <div>
                      <div>Lugar: {o.place || '—'}</div>
                      <div>Supervisor cliente: {o.client_supervisor || o.clientSupervisor || '—'}</div>
                      <div>Lat/Lng: {(o.lat || '—')} / {(o.lng || '—')}</div>
                      <div>Zona/Municipio: {(o.zone || '—')} / {(o.municipality || '—')}</div>
                      <div>Supervisor Senerfi: {o.requestNumber || '—'}</div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
          {(Array.isArray(completeForm.nodes) && completeForm.nodes.length > 0) && (
            <div className="bg-gray-50 border rounded p-3">
              <div className="text-sm font-semibold mb-2">Resumen de nodos</div>
              <div className="text-xs text-gray-700">{completeForm.nodes.length} nodo(s) registrados</div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Estado</label>
              <select className="w-full border rounded px-3 py-2 bg-white" value={completeForm.status} onChange={(e) => setCompleteForm({ ...completeForm, status: e.target.value })}>
                <option value="En progreso">En proceso</option>
                <option value="Completado">Completado</option>
                <option value="Cancelado">Cancelado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hora inicio</label>
              <input type="time" className="w-full border rounded px-3 py-2" value={completeForm.startTime} onChange={(e) => setCompleteForm({ ...completeForm, startTime: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hora fin</label>
              <input type="time" className="w-full border rounded px-3 py-2" value={completeForm.endTime} onChange={(e) => setCompleteForm({ ...completeForm, endTime: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Número de solicitud</label>
              <input type="text" className="w-full border rounded px-3 py-2" value={completeForm.requestNumber || ''} onChange={(e) => setCompleteForm({ ...completeForm, requestNumber: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Lugar</label>
              <input type="text" className="w-full border rounded px-3 py-2" value={completeForm.place} onChange={(e) => setCompleteForm({ ...completeForm, place: e.target.value })} />
            </div>
          </div>
      <div>
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium mb-1">Nodos</label>
          <button type="button" className="px-2 py-1 text-xs rounded border" onClick={() => setCompleteForm({
            ...completeForm,
            nodes: [ ...(completeForm.nodes || []), { name: '', observations: '', materials: [], selectedActs: [], activityMinutes: {} } ],
          })}>Agregar nodo</button>
        </div>
        <div className="space-y-3">
          {(completeForm.nodes || []).map((n, idx) => (
            <div key={`node-${idx}`} className="border rounded p-2">
              <div className="flex items-center justify-between mb-2">
                <input type="text" placeholder="Nombre del nodo" className="flex-1 border rounded px-3 py-2 mr-2" value={n.name || ''} onChange={(e) => {
                  const next = [ ...(completeForm.nodes || []) ];
                  next[idx] = { ...(next[idx] || {}), name: e.target.value };
                  setCompleteForm({ ...completeForm, nodes: next });
                }} />
                <button type="button" className="px-2 py-1 text-xs rounded border" onClick={() => {
                  const next = (completeForm.nodes || []).filter((_, i) => i !== idx);
                  setCompleteForm({ ...completeForm, nodes: next });
                }}>Eliminar</button>
              </div>
              <div className="mb-2">
                <label className="block text-xs font-medium mb-1">Observaciones</label>
                <textarea className="w-full border rounded px-3 py-2 min-h-[60px]" value={n.observations || ''} onChange={(e) => {
                  const next = [ ...(completeForm.nodes || []) ];
                  next[idx] = { ...(next[idx] || {}), observations: e.target.value };
                  setCompleteForm({ ...completeForm, nodes: next });
                }} />
              </div>
              <div className="mb-2">
                <label className="block text-xs font-medium mb-1">Notas de actividad del nodo</label>
                <textarea className="w-full border rounded px-3 py-2 min-h-[60px]" value={n.notes || ''} onChange={(e) => {
                  const next = [ ...(completeForm.nodes || []) ];
                  next[idx] = { ...(next[idx] || {}), notes: e.target.value };
                  setCompleteForm({ ...completeForm, nodes: next });
                }} />
              </div>
              <div className="mb-2">
                <label className="block text-xs font-medium mb-1">Actividades del nodo</label>
                <div className="space-y-2">
                  {configActs.length === 0 ? (
                    <div className="text-xs text-gray-600">No hay actividades configuradas.</div>
                  ) : (
                    <div>
                      <input
                        type="text"
                        placeholder="Buscar actividad…"
                        className="w-full border rounded px-3 py-2 text-sm"
                        value={String(n.actQuery || '')}
                        onChange={(e) => {
                          const next = [ ...(completeForm.nodes || []) ];
                          next[idx] = { ...(next[idx] || {}), actQuery: e.target.value };
                          setCompleteForm({ ...completeForm, nodes: next });
                        }}
                      />
                      {String(n.actQuery || '').trim() && (
                        <div className="mt-1 border rounded max-h-40 overflow-auto">
                          {configActs
                            .filter((a) => String(a.name || '').toLowerCase().includes(String(n.actQuery || '').toLowerCase()))
                            .slice(0, 12)
                            .map((a) => {
                              const already = Array.isArray(n.selectedActs) ? n.selectedActs.includes(a.id) : false;
                              return (
                                <button
                                  key={`sugg-${idx}-${a.id}`}
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                  onClick={() => {
                                    const next = [ ...(completeForm.nodes || []) ];
                                    const sel = already ? (n.selectedActs || []) : [ ...(n.selectedActs || []), a.id ];
                                    next[idx] = { ...(next[idx] || {}), selectedActs: sel, actQuery: '' };
                                    setCompleteForm({ ...completeForm, nodes: next });
                                  }}
                                >
                                  {a.name}
                                </button>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                  {(Array.isArray(n.selectedActs) ? n.selectedActs : []).length > 0 && (
                    <ul className="space-y-1 text-sm">
                      {(n.selectedActs || []).map((id) => {
                        const a = configActs.find((x) => String(x.id) === String(id));
                        return (
                          <li key={`sel-${idx}-${id}`} className="flex items-center gap-2">
                            <span className="flex-1">{a?.name || id}</span>
                            <span className="text-xs text-gray-600">Minutos</span>
                            <input
                              type="number"
                              min="0"
                              className="w-20 border rounded px-2 py-1 text-xs"
                              value={String(((n.activityMinutes || {})[String(id)] || ''))}
                              onChange={(e) => {
                                const next = [ ...(completeForm.nodes || []) ];
                                const mMap = { ...(n.activityMinutes || {}), [String(id)]: e.target.value };
                                next[idx] = { ...(next[idx] || {}), activityMinutes: mMap };
                                setCompleteForm({ ...completeForm, nodes: next });
                              }}
                            />
                            <button
                              type="button"
                              className="px-2 py-1 text-xs rounded border"
                              onClick={() => {
                                const next = [ ...(completeForm.nodes || []) ];
                                const sel = (n.selectedActs || []).filter((x) => x !== id);
                                const mMap = { ...(n.activityMinutes || {}) };
                                delete mMap[String(id)];
                                next[idx] = { ...(next[idx] || {}), selectedActs: sel, activityMinutes: mMap };
                                setCompleteForm({ ...completeForm, nodes: next });
                              }}
                            >
                              Quitar
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
              <div>
              <label className="block text-xs font-medium mb-1">Materiales del nodo</label>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">
                  {technicianLoading ? 'Cargando stock...' : (technicianStock !== null ? 'Stock del técnico' : 'Stock por bodega')}
                </span>
                <div className="flex items-center gap-2">
                  <select
                    className="border rounded px-2 py-1 text-xs bg-white"
                    value={String(technicianIdentifier || '')}
                    onChange={(e) => {
                      const v = String(e.target.value || '');
                      setTechnicianIdentifier(v);
                      if (v) {
                        fetchTechnicianStock(v);
                      } else {
                        setTechnicianStock(null);
                      }
                    }}
                  >
                    <option value="">Stock por bodega</option>
                    {technicianOptions.map((p) => {
                      const ced = p.cedula || p.documento_identidad || p.dni || p.documento || p.doc || '';
                      const val = ced || p.id || '';
                      const nombre = p.name || p.nombre_completo || '';
                      return (
                        <option key={`tech-${val || nombre}`} value={val}>
                          {ced ? `${nombre} (${ced})` : nombre}
                        </option>
                      );
                    })}
                  </select>
                  {syncMsg && (<span className="text-xs text-gray-600">{syncMsg}</span>)}
                  <button type="button" onClick={syncMaterials} className="px-2 py-1 text-xs rounded border inline-flex items-center gap-1 shadow-sm hover:shadow-md transition-shadow" style={{ backgroundColor: '#E0F2FE', borderColor: '#93C5FD' }}><RefreshCcw size={14} /> Sincronizar</button>
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <input
                    type="text"
                    placeholder="Buscar material…"
                      className="w-full border rounded px-3 py-2 text-sm"
                      value={String(n.matQuery || '')}
                      onChange={(e) => {
                        const next = [ ...(completeForm.nodes || []) ];
                        next[idx] = { ...(next[idx] || {}), matQuery: e.target.value };
                        setCompleteForm({ ...completeForm, nodes: next });
                      }}
                    />
                    {String(n.matQuery || '').trim() && (
                      <div className="mt-1 border rounded max-h-40 overflow-auto">
                        {availableMaterials
                          .filter((m) => [m.code, m.name, m.brand, m.category].some((v) => String(v ?? '').toLowerCase().includes(String(n.matQuery || '').toLowerCase())))
                          .slice(0, 12)
                          .map((m) => {
                            const stk = Number((m.stockByWarehouse?.[warehouseId] || 0)) || 0;
                            const disabled = stk <= 0;
                            return (
                              <button
                                key={`msugg-${idx}-${m.id || m.code}`}
                                type="button"
                                disabled={disabled}
                                className={`w-full text-left px-3 py-2 text-sm ${disabled ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                                onClick={() => {
                                  if (disabled) return;
                                  const next = [ ...(completeForm.nodes || []) ];
                                  const mats = [ ...(n.materials || []), { name: m.name || '', code: m.code || '', qty: Math.min(1, stk), max: stk } ];
                                  next[idx] = { ...(next[idx] || {}), materials: mats, matQuery: '' };
                                  setCompleteForm({ ...completeForm, nodes: next });
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <span>{(m.name || '')} {(m.code ? '(' + m.code + ')' : '')}</span>
                                  <span className="text-xs">{disabled ? 'Sin stock en bodega' : `Disp.: ${stk}`}</span>
                                </div>
                              </button>
                            );
                          })}
                      </div>
                    )}
                  </div>
                  {(n.materials || []).map((m, mi) => (
                    <div key={`nmat-${idx}-${mi}`} className="grid grid-cols-5 gap-2 items-center">
                      <input type="text" placeholder="Material" className="col-span-2 border rounded px-3 py-2 bg-gray-100" value={m.name || ''} readOnly />
                      <input type="number" min="0" placeholder="Cantidad" className="border rounded px-3 py-2" value={String(m.qty ?? '')} onChange={(e) => {
                        const next = [ ...(completeForm.nodes || []) ];
                        const mats = [ ...(n.materials || []) ];
                        const v = Number(e.target.value);
                        const mx = Number(m.max || Infinity);
                        const clamped = Number.isFinite(v) ? Math.max(0, Math.min(v, mx)) : 0;
                        mats[mi] = { ...(mats[mi] || {}), qty: clamped };
                        next[idx] = { ...(next[idx] || {}), materials: mats };
                        setCompleteForm({ ...completeForm, nodes: next });
                      }} />
                      <span className="text-xs text-gray-600">Disp.: {Number(m.max || 0)} · Restante: {Math.max(0, Number(m.max || 0) - Number(m.qty || 0))}</span>
                      <button type="button" className="px-2 py-1 text-xs rounded border" onClick={() => {
                        const next = [ ...(completeForm.nodes || []) ];
                        const mats = (n.materials || []).filter((_, i) => i !== mi);
                        next[idx] = { ...(next[idx] || {}), materials: mats };
                        setCompleteForm({ ...completeForm, nodes: next });
                      }}>Quitar</button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1 mt-3">Material Retirado</label>
                <div className="text-xs text-gray-500 mb-1">
                   Agregar ítems retirados (solo tipo Material). No afecta inventario del técnico.
                </div>
                <div className="space-y-2">
                  <div>
                    <input
                      type="text"
                      placeholder="Buscar material retirado…"
                      className="w-full border rounded px-3 py-2 text-sm"
                      value={String(n.retiredQuery || '')}
                      onChange={(e) => {
                        const next = [ ...(completeForm.nodes || []) ];
                        next[idx] = { ...(next[idx] || {}), retiredQuery: e.target.value };
                        setCompleteForm({ ...completeForm, nodes: next });
                      }}
                    />
                    {String(n.retiredQuery || '').trim() && (
                      <div className="mt-1 border rounded max-h-40 overflow-auto bg-white shadow-lg z-10 relative">
                        {allMaterials
                          .filter((m) => {
                             const isTypeMat = String(m.type || 'Material').toLowerCase() === 'material';
                             return isTypeMat && [m.code, m.name].some((v) => String(v ?? '').toLowerCase().includes(String(n.retiredQuery || '').toLowerCase()));
                          })
                          .slice(0, 12)
                          .map((m) => (
                            <button
                              key={`ret-sugg-${idx}-${m.id || m.code}`}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 border-b last:border-0"
                              onClick={() => {
                                const next = [ ...(completeForm.nodes || []) ];
                                const currentRet = n.retiredMaterials || [];
                                const newItem = { name: m.name || '', code: m.code || '', qty: 1 };
                                next[idx] = { ...(next[idx] || {}), retiredMaterials: [...currentRet, newItem], retiredQuery: '' };
                                setCompleteForm({ ...completeForm, nodes: next });
                              }}
                            >
                              <span>{(m.name || '')} {(m.code ? '(' + m.code + ')' : '')}</span>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                  {(n.retiredMaterials || []).map((m, mi) => (
                    <div key={`nret-${idx}-${mi}`} className="grid grid-cols-5 gap-2 items-center">
                      <input type="text" placeholder="Material" className="col-span-2 border rounded px-3 py-2 bg-gray-100" value={m.name || ''} readOnly />
                      <input type="number" min="0" placeholder="Cantidad" className="border rounded px-3 py-2" value={String(m.qty ?? '')} onChange={(e) => {
                        const next = [ ...(completeForm.nodes || []) ];
                        const list = [ ...(n.retiredMaterials || []) ];
                        list[mi] = { ...list[mi], qty: Number(e.target.value) };
                        next[idx] = { ...(next[idx] || {}), retiredMaterials: list };
                        setCompleteForm({ ...completeForm, nodes: next });
                      }} />
                      <div className="col-span-2 flex justify-end">
                        <button type="button" className="px-2 py-1 text-xs rounded border" onClick={() => {
                          const next = [ ...(completeForm.nodes || []) ];
                          const list = (n.retiredMaterials || []).filter((_, i) => i !== mi);
                          next[idx] = { ...(next[idx] || {}), retiredMaterials: list };
                          setCompleteForm({ ...completeForm, nodes: next });
                        }}>Quitar</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Latitud</label>
          <input type="text" className="w-full border rounded px-3 py-2" value={completeForm.lat} onChange={(e) => setCompleteForm({ ...completeForm, lat: e.target.value })} />
        </div>
            <div>
              <label className="block text-sm font-medium mb-1">Longitud</label>
              <input type="text" className="w-full border rounded px-3 py-2" value={completeForm.lng} onChange={(e) => setCompleteForm({ ...completeForm, lng: e.target.value })} />
            </div>
          </div>
          
          
          <div>
            <label className="block text-sm font-medium mb-1">Trabajo realizado</label>
            <textarea className="w-full border rounded px-3 py-2 min-h-[100px]" value={completeForm.doneWork} onChange={(e) => setCompleteForm({ ...completeForm, doneWork: e.target.value })} />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Observaciones</label>
            <textarea className="w-full border rounded px-3 py-2 min-h-[80px]" value={completeForm.notes} onChange={(e) => setCompleteForm({ ...completeForm, notes: e.target.value })} />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => setIsCompleteOpen(false)} className="px-4 py-2 rounded bg-gray-200 text-gray-800">Cancelar</button>
            <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white"><Save size={16} /> Guardar</button>
          </div>
        </form>
      </Modal>
      <Modal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} title="Autorización para eliminar orden" size="max-w-md">
        <div className="space-y-3">
          <p className="text-xs text-gray-700">Para eliminar una orden se requiere autorización de un usuario con rol Administrador.</p>
          {String(currentUser?.role || '').toLowerCase() === 'administrador' ? (
            <div className="text-xs text-green-700">Estás autenticado como Administrador. Puedes eliminar directamente.</div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              <input type="email" placeholder="Correo administrador" className="border rounded px-3 py-2" value={delAuthEmail} onChange={(e) => setDelAuthEmail(e.target.value)} />
              <input type="password" placeholder="Contraseña" className="border rounded px-3 py-2" value={delAuthPass} onChange={(e) => setDelAuthPass(e.target.value)} />
            </div>
          )}
          {delAuthMsg && (<p className="text-xs text-red-600">{delAuthMsg}</p>)}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => setIsDeleteOpen(false)} className="px-3 py-1 rounded border">Cancelar</button>
            <button type="button" onClick={confirmDeleteWithAuth} className="px-3 py-1 rounded bg-red-600 text-white">Eliminar</button>
          </div>
        </div>
      </Modal>
      <Modal isOpen={isReprogOpen} onClose={() => setIsReprogOpen(false)} title="Reprogramar Orden" size="max-w-md">
        <div className="space-y-3">
          {reprogMsg && (<p className="text-xs text-red-600">{reprogMsg}</p>)}
          <div>
            <label className="block text-sm font-medium mb-1">Brigada</label>
            <select className="w-full border rounded px-3 py-2 bg-white" value={reprogBrigId} onChange={(e) => setReprogBrigId(e.target.value)}>
              <option value="">Seleccionar brigada</option>
              {brigades.map((b) => (
                <option key={`rb-${b.id}`} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium mb-1">Fecha</label>
              <input type="date" className="w-full border rounded px-3 py-2" value={reprogDate} onChange={(e) => setReprogDate(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hora</label>
              <input type="time" className="w-full border rounded px-3 py-2" value={reprogTime} onChange={(e) => setReprogTime(e.target.value)} />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => setIsReprogOpen(false)} className="px-3 py-1 rounded border">Cancelar</button>
            <button type="button" onClick={confirmReprogram} className="px-3 py-1 rounded" style={{ backgroundColor: '#FDE68A', color: '#1F2937' }}>Reprogramar</button>
          </div>
        </div>
      </Modal>
      <Modal isOpen={isEditBrigOpen} onClose={() => setIsEditBrigOpen(false)} title="Editar Brigada" size="max-w-lg">
        <form onSubmit={saveEditBrigade} className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Nombre</label>
            <input type="text" className="w-full border rounded px-3 py-2" value={editBrigForm.name} onChange={(e) => setEditBrigForm({ ...editBrigForm, name: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Descripción</label>
            <input type="text" className="w-full border rounded px-3 py-2" value={editBrigForm.description} onChange={(e) => setEditBrigForm({ ...editBrigForm, description: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Responsable</label>
            <select className="w-full border rounded px-3 py-2 bg-white" value={editBrigForm.responsibleId} onChange={(e) => setEditBrigForm({ ...editBrigForm, responsibleId: e.target.value })}>
              <option value="">Seleccionar responsable</option>
              {activeEmployees.map((p) => {
                const ced = p.cedula || p.documento_identidad || p.dni || p.documento || p.doc || '';
                const val = (typeof p.id !== 'undefined' && p.id !== null) ? p.id : ced;
                const nombre = p.name || p.nombre_completo || '';
                return (
                  <option key={`eresp-${val || nombre}`} value={val}>{`${nombre} (${ced || val || 'N/D'})`}</option>
                );
              })}
            </select>
          </div>
          <div>
            <div className="text-sm font-medium mb-1">Miembros</div>
            <div className="max-h-40 overflow-auto border rounded p-2">
              <ul className="space-y-1">
                {activeEmployees.map((p) => {
                  const id = (typeof p.id !== 'undefined' && p.id !== null) ? p.id : (p.cedula || p.documento_identidad || p.dni || p.documento || p.doc || '');
                  const name = p.name || p.nombre_completo || String(id);
                  const ced = p.cedula || p.documento_identidad || p.dni || p.documento || p.doc || '';
                  const checked = (editBrigForm.members || []).includes(id);
                  return (
                    <li key={`em-${id}`} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={checked} onChange={() => toggleEditBrigMember(id)} />
                      <span>{ced ? `${name} (${ced})` : name}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => setIsEditBrigOpen(false)} className="px-3 py-1 rounded border">Cancelar</button>
            <button type="submit" className="px-3 py-1 rounded text-white" style={{ backgroundColor: '#776F74' }}>Guardar cambios</button>
          </div>
        </form>
      </Modal>
  </div>
);
}
